// ===================
//  Synth Cue Support
// ===================
//
// synth.js — WebAudio synth voices and their pattern sequencing
//
// Key semantics (v1.2):
// - A synth voice is created by synth(...) and identified by uid.
// - By default, synth voices are SAFE: they auto-stop
//     (a) when the playhead leaves the cue element's bbox (lifetime:region),
//     OR (b) when a provided duration elapses (dur / stopAfter).
// - If you want a persistent synth, set lifetime:process explicitly.
//
// It is made as audioFile.js is:
// - shared AudioContext
// - uid registry
// - safe gain ramps
// - optional scope info text (SVG-native, in renderScope)
//
// Exports:
// - handleSynthCue(ast, cueElement?, opts?)
// - stopSynth(uidOrAst, rel?)
// - checkSynthRegions()   (call this from your main tick, like checkImpulseRegions)
// - primeSynthOverlay(ast, cueElement)  (called during assignCues)
// - primeSynthScope(ast, cueElement)    (oscilloscope priming, called from primeSynthOverlay)
//
// ------------------------------------------------------------

import { isObjectTrig } from "./probeTriggers.js";
import { isAuthoredExtentGroup } from "../parser/cueUtils.js";
import { playheadScreenX } from "../system/paths.js";
import "./osc.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { bindParam, isBindable, isSignalRef } from '../control/paramBinding.js';
import { pulse } from '../control/liveSignals.js';   // also starts the beat / level signals
import { renderScope, startScope, stopScope, fadeScope } from "../system/scope.js";
import { secs, secsOf, secPerBeat } from "./beats.js";
import { getWaveform, destroyWaveform } from "../system/waveform.js";
import { createLiveAudioRect, nextLiveColor } from "./audio/audioFile.js";
import { evalMaybeRandom, getPlaybackClientsFromEl, synthOutput, formatPatternRepeats, makePatternGenerator } from "./audio/audioShared.js";
import { makeEvery } from "./audio/patterns.js";
import { activeSynthVoices } from "./audio/voices.js";
import { buildReverb, updateReverb, disconnectReverb } from "./audio/reverb.js";
import { channelFor } from "./audio/level.js";
import { isModulator, modSpec, attachModulator } from "./audio/modulators.js";
import { buildFuzz, loadFuzz } from "./audio/fuzz.js";
import { buildSub, loadSub } from "./audio/sub.js";
import { buildGrit, loadGrit } from "./audio/grit.js";
import { FB_MAX } from "./audio/feedback.js";
import { loadNoiseGen, genSource, inputSource, GEN_WAVES, INPUT_WAVES } from "./audio/sources.js";
import { levelCurve } from "./audio/level.js";

import { nextGridBoundary, gridNow, beatsToSec, beatAt } from "./audio/beatGrid.js";
import { outputLatencyMs } from "../system/outputLatency.js";
import { degreesToPitches, degreeToMidi } from "./audio/scales.js";
import { registerAnalyser, unregisterAnalyser } from "./audio/liveAnalysers.js";
import { fmSpec, attachFM, fmStrike, driveCurve, drumParams, kickStrike } from "./audio/synthTimbre.js";
import { sendTo, sendCopy } from "./audio/fxBus.js";
import { setVuMode } from "./audio/vuModes.js";
import { setPitchResolver, compilePipe, pipeValue } from "./audio/pitchSieve.js";
import { hasRel, resolveRelArgs, isRel } from "./relative.js";
import { buildFilter, buildDelay, clamp } from "./audio/effects.js";
import { spatialPanner, bindSpatial, hasSpatial, toBus, SPATIAL_KEYS } from "./audio/speakers.js";
import { onReset } from "../system/teardown.js";

// ============================================================
// 🌐 Shared AudioContext + registry
// ============================================================
export const sharedAudioCtx =
  window.sharedAudioCtx ||
  (window.sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)());

// the fuzz is loaded into the audio engine now, so it is ready when asked for
try { loadFuzz(sharedAudioCtx).catch(() => {}); loadSub(sharedAudioCtx).catch(() => {}); loadGrit(sharedAudioCtx).catch(() => {}); loadNoiseGen(sharedAudioCtx).catch(() => {}); } catch { /* no AudioWorklet: they play clean */ }

export { activeSynthVoices };

// Polyphony: track voice groups and per-group voice counters
// polyGroups: Map<baseUid, { max: number, subUids: string[] }>
const polyGroups = window._synthPolyGroups || (window._synthPolyGroups = new Map());

// Starts waiting for their beat (quant:, below): uid -> Set of { timer, done }.
// hush() and stopSynth() cancel them -- a pattern run from the console waits
// up to a step for the grid (4 beats for a dur:4 pad), and one left waiting
// started after the stage was cleared: a previous example's box in the next.
const pendingStarts = new Map();
function cancelPendingStarts(uid = null) {
  for (const [u, set] of pendingStarts) {
    if (uid != null && u !== uid && !u.startsWith(`${uid}__`)) continue;
    for (const p of set) { clearTimeout(p.timer); p.done(null); }
    pendingStarts.delete(u);
  }
}
let polyCounter = 0;

/** Set opacity on an info text element (works for both SVG <text> and HTML <div>) */
function setInfoOpacity(el, val) {
  if (!el) return;
  if (el.style !== undefined) el.style.opacity = val;
  else el.setAttribute("opacity", val);
}

/** Resolve a param value that might be a rand/irand expression to a number. */
function resolveVal(v, fallback) {
  if (v == null) return fallback;
  if (typeof v === "number") return v;
  if (typeof v === "object" && (v.type === "rand" || v.type === "irand" || v.type === "funcCall")) {
    const r = evalMaybeRandom(v);
    return (r != null && typeof r === "number") ? r : fallback;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function nowSec(ctx) {
  return ctx.currentTime;
}

// ============================================================
// 🎨 Scope color by synth type
// ============================================================
const SCOPE_COLOR_SINGLE = "#33aa66";   // Green - single tone
const SCOPE_COLOR_CHORD = "#6699ff";    // Blue - chord (simultaneous)
const SCOPE_COLOR_SEQUENCE = "#ff9933"; // Orange - sequence (stepping)

/**
 * Determine the scope color based on synth type.
 * @param {object} params - synth params containing freq
 * @returns {string} hex color for scope
 */
function getSynthTypeColor(params) {
  const freq = params?.freq;
  if (freq == null) return SCOPE_COLOR_SINGLE;

  // Chord: array of frequencies (simultaneous playback)
  if (Array.isArray(freq) && freq.length > 1) {
    return SCOPE_COLOR_CHORD;
  }

  // Sequence: pattern object (stepping through values)
  if (typeof freq === "object" && freq.type === "pattern") {
    return SCOPE_COLOR_SEQUENCE;
  }

  // Single tone
  return SCOPE_COLOR_SINGLE;
}

// Convert legacy AST args list into a params object.
// Supports both:
//  - {type:"cueSynth", args:[{type:"uid", value:"x"}, ...]}
//  - {type:"cueSynth", params:{...}} (future)
//  - direct {uid, freq, ...} (manual calls)
function extractParams(ast) {
  const raw = rawParams(ast);
  // wave:choose(square, noise, triangle) / rand([...]): one of them. (The
  // live console rolls it before the line runs; a score's line arrives here
  // with the call itself.) Before the drums: wave:choose(kick, hat) works too.
  if (raw.wave && typeof raw.wave === "object" && raw.wave.type === "funcCall") raw.wave = evalMaybeRandom(raw.wave);
  // wave:Pchoose(square, noise, triangle) -- a wave each step (applyStepTargets);
  // the voice starts on the pattern's first
  let wavePattern = null;
  if (raw.wave && typeof raw.wave === "object" && raw.wave.type === "pattern") {
    wavePattern = raw.wave;
    raw.wave = String(wavePattern.values?.[0] ?? "sine");
  }
  // The line's times are beats (beats.js): from here on seconds -- its
  // envelope, glide:, fades, the delay's, the reverb's, fm:'s decay. Before the
  // drums: a kick's own envelope is its sound, not a time in the music.
  // (dur:, quant: and stopAfter: are counted on the beat grid where used.)
  const k = secPerBeat();
  if (k !== 1) {
    const ENV = ["a", "d", "r", "attack", "decay", "release"];
    const times = { env: v => secsOf(v, ENV, k), envPerStep: v => secsOf(v, ENV, k),
      delay: v => secsOf(v, ["time"], k), reverb: v => secsOf(v, ["time", "pre", "predelay"], k),
      fm: v => secsOf(v, ["decay"], k) };
    for (const key of ["glide", "fadeIn", "fadein", "fadeOut", "fadeout", "rel", "release"]) times[key] = v => secs(v, k);
    for (const key in times) if (raw[key] != null) raw[key] = times[key](raw[key]);
  }
  // wave:kick / snare / hat: a drum's defaults under what the line says
  const p = drumParams(raw);
  if (wavePattern) p.wavePattern = wavePattern;
  // envPerStep:{a, d, s, r} -- the envelope each step is struck with, and
  // envPerStep on (until 12 Oct 2026 the numbers were ignored: it only
  // switched per-step on, with env:'s envelope, full sustain by default)
  // With both, env: is the whole sound's: it swells in over its a: when the
  // sound starts (and seq(s, 1)), and at seq(s, 0) its last note rings on
  // and dies away over its r:. Each note is struck with envPerStep's.
  if (p.envPerStep && typeof p.envPerStep === "object" && !Array.isArray(p.envPerStep)) {
    if (p.env && typeof p.env === "object" && !Array.isArray(p.env)) p.wholeEnv = { ...p.env };
    p.env = { ...(p.env && typeof p.env === "object" ? p.env : {}), ...p.envPerStep };
    p.envPerStep = true;
  }
  // degree: in a scale: from a root: (scales.js) -- as freq: would be written
  if (p.degree != null) p.freq = degreesToPitches(p.degree, p.scale, p.root);
  return p;
}

function rawParams(ast) {
  if (!ast) return {};

  if (ast.params && typeof ast.params === "object") {
    // Return a shallow copy to prevent shared mutable state issues
    return { ...ast.params };
  }

  if (Array.isArray(ast.args)) {
    const out = {};
    for (const a of ast.args) {
      if (!a || typeof a !== "object") continue;
      if (!a.type) continue;
      out[a.type] = a.value;
    }
    return out;
  }

  // fallback: treat ast itself as params
  const out = { ...ast };
  delete out.type;
  delete out.args;
  return out;
}

function isOscEnabled(ast, params) {
  const osc = Number(params?.osc ?? ast?.osc ?? 0) || 0;
  const addr = params?.oscAddr ?? params?.oscaddr ?? ast?.oscAddr ?? ast?.oscaddr;
  return osc > 0 || !!addr;
}

function sendOSCSynth(ast, params, payload) {
  const addr = params?.oscAddr ?? params?.oscaddr ?? ast?.oscAddr ?? ast?.oscaddr ?? null;

  const out = {
    type: "osc_synth",
    uid: params?.uid ?? ast?.uid,
    timestamp: Date.now(),
    ...payload
  };

  if (addr) out.addr = addr;

  try {
    sendOSC(out);
  } catch {
    /* ignore */
  }
}

// ============================================================
// 🎛 Noise buffers (cached per type)
// ============================================================
const _noiseBuffers = {};

/**
 * Get a loopable noise buffer for the given colour.
 *   white — flat spectrum (equal energy per Hz)
 *   pink  — −3 dB/octave (equal energy per octave, Paul Kellet algorithm)
 *   brown — −6 dB/octave (integrated white noise, aka red/Brownian)
 */
function getNoiseBuffer(ctx, seconds = 2, colour = "white") {
  const key = colour + "_" + ctx.sampleRate;
  if (_noiseBuffers[key]) return _noiseBuffers[key];

  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);

  if (colour === "pink") {
    // Paul Kellet's refined pink noise algorithm
    // Six first-order filters shape white noise to −3 dB/octave
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.96900 * b2 + w * 0.1538520;
      b3 = 0.86650 * b3 + w * 0.3104856;
      b4 = 0.55000 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.0168980;
      data[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362;
      b6 = w * 0.115926;
    }
    // Normalize to ±1
    let peak = 0;
    for (let i = 0; i < len; i++) { const a = Math.abs(data[i]); if (a > peak) peak = a; }
    if (peak > 0) { const g = 1 / peak; for (let i = 0; i < len; i++) data[i] *= g; }

  } else if (colour === "brown") {
    // Brownian / red noise: integrate white noise, −6 dB/octave
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.02 * w) / 1.02; // leaky integrator
      data[i] = last;
    }
    // Normalize to ±1
    let peak = 0;
    for (let i = 0; i < len; i++) { const a = Math.abs(data[i]); if (a > peak) peak = a; }
    if (peak > 0) { const g = 1 / peak; for (let i = 0; i < len; i++) data[i] *= g; }

  } else {
    // White noise — flat spectrum
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  }

  _noiseBuffers[key] = buf;
  return buf;
}

// ============================================================
// 🎚 Envelope helpers (ADSR)
// ============================================================
function normaliseEnv(env) {
  if (!env || typeof env !== "object") return { a: 0.02, d: 0.0, s: 1.0, r: 0.1 };

  const a = env.a ?? env.attack ?? 0.02;
  const d = env.d ?? env.decay ?? 0.0;
  const s = env.s ?? env.sustain ?? 1.0;
  const r = env.r ?? env.release ?? 0.1;

  return {
    a: clamp(a, 0, 60),
    d: clamp(d, 0, 60),
    s: clamp(s, 0, 1),
    r: clamp(r, 0, 60)
  };
}

function applyADSR(gainParam, ctx, t0, amp, env) {
  // Minimum 3ms attack to prevent click from instantaneous onset
  const A = Math.max(0.003, env.a);
  const D = Math.max(0, env.d);
  const S = clamp(env.s, 0, 1);

  const peak = amp;
  const sus = amp * S;

  // Schedule from current time if t0 is in the past
  const now = ctx.currentTime;
  const start = Math.max(t0, now);

  try {
    gainParam.cancelScheduledValues(start);
    gainParam.setValueAtTime(0, start);
    gainParam.linearRampToValueAtTime(peak, start + A);
    if (D > 0) gainParam.linearRampToValueAtTime(sus, start + A + D);
    else gainParam.setValueAtTime(sus, start + A);
  } catch (e) {
    console.error("[ADSR] Error scheduling envelope:", e);
  }
}


// ============================================================
// 🎼 Pitch parsing
// ============================================================

/**
 * Check if a value represents a rest (silence).
 * Supports: r, ~r, rest, ~rest (case insensitive)
 */
function isRest(v) {
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s === "r" || s === "~r" || s === "rest" || s === "~rest";
}

/**
 * stack:[0, 7] -- every note with these above it, in semitones (0 the note
 * itself): parallel fifths; stack:[0, 3, 7] a minor triad on each. A bare
 * number is the one added: stack:7 is [0, 7]. A chord stacks each of its notes.
 */
function stackNotes(v, stack) {
  const iv = Array.isArray(stack) ? stack.map(Number) : [0, Number(stack)];
  const steps = iv.filter(Number.isFinite);
  if (!steps.length) return v;
  return [].concat(v).flatMap(n => { const hz = pitchToHz(n); return steps.map(s => hz * 2 ** (s / 12)); });
}

/** octave:N -- a note (or each of a chord's) N octaves up, or down: N a number or a rand(). */
function shiftOctaves(v, octave) {
  const n = Number(evalMaybeRandom(octave)) || 0;
  if (!n) return v;
  return Array.isArray(v) ? v.map(f => pitchToHz(f) * 2 ** n) : pitchToHz(v) * 2 ** n;
}

/** transpose:N -- a note (or each of a chord's) N semitones up, or down. */
function shiftSemitones(v, n) {
  if (!n) return v;
  return Array.isArray(v) ? v.map(f => pitchToHz(f) * 2 ** (n / 12)) : pitchToHz(v) * 2 ** (n / 12);
}

// a bar: four beats of the shared grid (every(4, beat.hit) is a bar's first)
const BEATS_PER_BAR = 4;

/**
 * transpose:pseq(0, 0, 0, 0, 5, 5, 0, 0, 7, 5, 0, 7, inf) -- semitones, the
 * pattern a step a BAR, not a note: a 12-bar under any dur:. The bar is the
 * shared grid's at the step's time t. A pseq of values is at the clock's bar
 * of it (bar mod its length): every line with the same form at the same bar
 * of it, run when they may -- and every player's in a room. Any other
 * pattern draws a value each bar, a step that spans two bars drawing two.
 * A number (or rand()) is the same all through.
 */
function transposeAt(voice, t) {
  const raw = voice.params?.transpose;
  if (raw == null) return 0;
  if (!voice._transposeGen?._dynamic) return Number(evalMaybeRandom(raw)) || 0;
  if (voice._hold && voice._transposeNow != null) return voice._transposeNow;
  const a = gridToAudio();
  const bar = Math.floor(beatAt(a.ms + (t - a.sec) * 1000) / BEATS_PER_BAR + 1e-4);
  const vals = raw.name === "Pseq" && Array.isArray(raw.values) && raw.values.length && !raw.values.some(v => v?.type === "pattern") ? raw.values : null;
  if (vals) {
    if (voice._transposeBar !== bar) voice._transposeNow = Number(evalMaybeRandom(vals[((bar % vals.length) + vals.length) % vals.length])) || 0;
    voice._transposeBar = bar;
    return voice._transposeNow;
  }
  const last = voice._transposeBar;
  const moves = last == null ? 1 : Math.min(Math.max(0, bar - last), 64);
  for (let i = 0; i < moves; i++) voice._transposeNow = Number(evalMaybeRandom(draw(voice, "transpose", raw))) || 0;
  voice._transposeBar = bar;
  return voice._transposeNow ?? 0;
}

export function pitchToHz(v) {
  if (v == null) return 440;

  // a rand() in degree: (scales.js) -- drawn now, a degree of its scale
  if (typeof v === "object" && v.type === "funcCall" && v.name === "degree") {
    const m = degreeToMidi(evalMaybeRandom(v.args?.[0]), v.steps, v.root);
    return m == null ? 440 : clamp(440 * Math.pow(2, (m - 69) / 12), 1, 20000);
  }

  // Resolve rand()/irand() expressions before parsing
  if (typeof v === "object" && (v.type === "rand" || v.type === "irand" || v.type === "funcCall")) {
    const resolved = evalMaybeRandom(v);
    if (resolved != null && typeof resolved === "number") return clamp(resolved, 1, 20000);
    return 440;
  }

  if (typeof v === "number") return clamp(v, 1, 20000);

  const s = String(v).trim();
  const num = Number(s);
  if (Number.isFinite(num)) return clamp(num, 1, 20000);

  // MIDI note number: m60 = middle C, m69 = A4 = 440 Hz. Fractional numbers
  // give microtones (m60.5 = quarter tone above middle C).
  const midi = s.match(/^~?m(\d+(?:\.\d+)?)$/i);
  if (midi) {
    const n = Number(midi[1]);
    return clamp(440 * Math.pow(2, (n - 69) / 12), 1, 20000);
  }

  // note name token like ~A4, ~C#3, ~Bb2 with optional cents offset (~A4+50, ~C#3-25,
  // ~A4+12.5; ~G2+rand(0, 25): drawn afresh each time it is played -- a step, a run)
  // The ~ prefix is required in DSL but optional here for backwards compatibility with stored values
  const m = s.match(/^~?([A-Ga-g])([#b]?)(-?\d+)(?:([+-])(-?\d+(?:\.\d+)?|(rand|irand|birand)\(([^()]*)\)))?$/);
  if (!m) return 440;

  const note = m[1].toUpperCase();
  const accidental = m[2] || "";
  const oct = Number(m[3]);
  const drawn = m[6] ? Number(evalMaybeRandom({ type: "funcCall", name: m[6], args: m[7].split(",").map(Number) })) : null;
  const cents = !m[4] ? 0 : (m[4] === "-" ? -1 : 1) * (m[6] ? (Number.isFinite(drawn) ? drawn : 0) : Number(m[5]));

  const base = { C: -9, D: -7, E: -5, F: -4, G: -2, A: 0, B: 2 }[note] ?? 0;
  let semis = base;
  if (accidental === "#") semis += 1;
  if (accidental === "b") semis -= 1;

  const n = semis + (oct - 4) * 12;
  const baseHz = 440 * Math.pow(2, n / 12);

  // Apply cents offset (100 cents = 1 semitone)
  if (cents !== 0) {
    return clamp(baseHz * Math.pow(2, cents / 1200), 1, 20000);
  }
  return baseHz;
}

// value | scale(minor): the pitch filters read values as freq: does (pitchSieve.js)
setPitchResolver(pitchToHz);

// ============================================================
// 🎛 Patterns
// ============================================================

function buildParamGen(raw) {
  if (raw == null) return null;

  // Pattern object (from parser). The shared generator returns null for an
  // empty pattern, so an empty Pseq() must not become a call to null.next() here.
  if (typeof raw === "object" && raw.type === "pattern") {
    const gen = makePatternGenerator(raw);
    if (!gen) return { next: () => null, lastIndex: 0, _dynamic: false };
    return {
      next: () => gen.next(),
      get lastIndex() { return gen.lastIndex; },
      _dynamic: true
    };
  }

  // Array shorthand -> Pseq
  if (Array.isArray(raw)) {
    const gen = makePatternGenerator({ name: "Pseq", values: raw, repeats: "inf" });
    if (!gen) return { next: () => null, lastIndex: 0, _dynamic: false };
    return {
      next: () => gen.next(),
      get lastIndex() { return gen.lastIndex; },
      _dynamic: true
    };
  }

  // Constant
  return { next: () => raw, lastIndex: 0, _dynamic: false };
}

// sinOsc() and its family on a parameter (modulators.js): one per voice and
// parameter. The same call run again keeps its modulator running (no jump);
// another one replaces it. True while the parameter is modulated -- then
// nothing else writes it.
function syncMod(voice, key, param, raw, { spec = null, scale = 1 } = {}) {
  const mods = voice._mods || (voice._mods = new Map());
  const cur = mods.get(key);
  const same = cur && JSON.stringify(cur.raw) === JSON.stringify(raw);
  if (same) return true;
  cur?.stop();
  mods.delete(key);
  if (!param || !isModulator(raw)) return false;
  const m = attachModulator(param, spec ?? raw, { scale });
  m.raw = raw;
  mods.set(key, m);
  return true;
}

// hold(s): every pattern on a sound stays on the value it drew last, the
// rhythm going on -- hold(s, 0) lets them carry on from there (hold.js).
// Each draw goes through here and is remembered.
const heldNames = new Set();
// A rand() a pattern draws (Prand(150, rand(20, 390))) is rolled here, so
// what is remembered is the number: held, the same note -- not a new roll
// each step. (A chord's: each of its notes.)
const ROLLED = new Set(["rand", "irand", "birand", "hrand", "rnd", "choose", "exprand"]);
const rollDrawn = x => Array.isArray(x) ? x.map(rollDrawn)
  : x && typeof x === "object" && (["rand", "irand", "birand"].includes(x.type) || (x.type === "funcCall" && ROLLED.has(x.name)))
    ? evalMaybeRandom(x) : x;
function draw(voice, key, rebuildFrom) {
  const last = voice._drawn || (voice._drawn = new Map());
  if (voice._hold && last.has(key)) return last.get(key);
  const genKey = `_${key}Gen`;
  let v = voice[genKey].next();
  if (v == null && rebuildFrom !== undefined) { voice[genKey] = buildParamGen(rebuildFrom); v = voice[genKey]?.next?.(); }
  v = rollDrawn(v);
  last.set(key, v);
  return v;
}

/**
 * hold(name, on): hold every voice of the sound (poly ones too), and any it
 * starts -- its pattern on the step it is on, and its dur: (a chord kept
 * sounding past its time). Let go, each voice has the time it had left.
 */
export function holdSynth(name, on = true) {
  name = String(name);
  if (on) heldNames.add(name); else heldNames.delete(name);
  const now = performance.now();
  for (const [k, v] of activeSynthVoices) {
    if (k !== name && !k.startsWith(`${name}__`)) continue;
    v._hold = !!on;
    if (on && v._stopTimeout) {
      clearTimeout(v._stopTimeout); v._stopTimeout = null;
      v._lifeLeft = Math.max(0, v._lifeEnd - now);
    } else if (!on && v._lifeLeft != null) armLifetime(v, k, v._lifeLeft);
  }
  stateChanged();
}

/**
 * A voice's lifetime (dur:, stopAfter:): it stops `ms` from now -- or, held,
 * keeps the time for when it is let go (holdSynth).
 */
function armLifetime(voice, uid, ms) {
  if (voice._stopTimeout) { clearTimeout(voice._stopTimeout); voice._stopTimeout = null; }
  voice._lifeLeft = null;
  if (voice._hold) { voice._lifeLeft = ms; return; }
  voice._lifeEnd = performance.now() + ms;
  voice._stopTimeout = setTimeout(() => stopSynth(uid), ms);
}

// Each sound's line as written, rand()s unrolled -- what bang(s) runs again
const soundLines = new Map();
onReset("project", function forgetSoundLines() { soundLines.clear(); });   // bang(s) runs this project's s
// bang(s, every): each sound's metro (bang.js), stopped by hush()
const bangTimers = new Map();
/** Set (or with null, stop) the metro of sound `name`. */
export function setBangTimer(name, id) {
  const had = bangTimers.has(name);
  clearTimeout(bangTimers.get(name));
  if (id) bangTimers.set(name, id); else bangTimers.delete(name);
  if (had !== bangTimers.has(name)) stateChanged();     // the console tags it active, or not
}
/** The line sound `name` was last run with (bang.js), or null. */
export const synthLine = name => soundLines.get(String(name)) ?? null;

/**
 * trig:wait (or trig:solo) -- the line learnt, not played: solo(name) and
 * bang(name) start it (cueDispatcher). Run with the rest of its block, the
 * sound is ready and silent.
 */
export const WAITS = /^(wait|solo)$/i;
const waitsIn = line => /\btrig\s*:\s*(wait|solo)\b/i.test(line);
/** A voice of a waiting sound came or went: the console's tag on its line follows. */
const waitTag = key => { if (waitsIn(soundLines.get(String(key).split("__")[0]) ?? "")) stateChanged(); };
export function learnSynthLine(name, line) {
  soundLines.set(String(name), String(line));
  stateChanged();
}

/** The sounds paused (gate(s, 0)), held (hold(s)) and banged by a metro (bang(s, 4)), by name -- the console tags their lines. */
export function soundStates() {
  const sounding = new Set([...(window.activeSynthVoices?.keys() || [])].map(k => String(k).split("__")[0]));
  const waiting = [...soundLines].filter(([n, l]) => waitsIn(l) && !sounding.has(n)).map(([n]) => n);
  return { gated: [...gatedNames], held: [...heldNames], banging: [...bangTimers.keys()], stopped: [...stoppedSeqs], waiting };
}
const stateChanged = () => window.dispatchEvent(new CustomEvent("oscilla:sound-state"));

/** hush(): what it stops is no longer held -- or gated, or banged. */
export function releaseHolds(names = null) {
  for (const n of names ? names.map(String) : [...bangTimers.keys()]) setBangTimer(n, null);   // and bang()'s metro
  for (const n of names ? names.map(String) : [...heldNames]) heldNames.delete(n);
  for (const n of names ? names.map(String) : [...gatedNames]) gatedNames.delete(n);
  for (const n of names ? names.map(String) : [...stoppedSeqs]) stoppedSeqs.delete(n);
  stateChanged();
}

// gate(s, 0 | 1): a sound's gate, closed or open (gate.js)
const gatedNames = new Set();

/**
 * Close (on false) or open a sound's gate: every voice of it, poly ones
 * too, and any it starts while closed. Over `time` seconds (by default the sound's
 * own release closing, its attack opening), along `curve`. Laid as short
 * straight ramps from where the gate is: cut and turned round cleanly.
 */
export function gateSynth(name, open, time = null, curve = "lin") {
  name = String(name);
  if (open) gatedNames.delete(name); else gatedNames.add(name);
  stateChanged();
  for (const [k, v] of activeSynthVoices) {
    if ((k !== name && !k.startsWith(`${name}__`)) || !v.graph?.gate) continue;
    const p = v.graph.gate.gain, t0 = sharedAudioCtx.currentTime + 0.01;
    const env = v.wholeEnv || v.env || { a: 0.02, r: 0.1 };
    const dur = Math.max(0.005, Number(time ?? (open ? env.a : env.r)) || 0.005);
    const shape = levelCurve(p.value, open ? 1 : 0, curve);
    try {
      if (p.cancelAndHoldAtTime) p.cancelAndHoldAtTime(t0); else { p.cancelScheduledValues(t0); p.setValueAtTime(p.value, t0); }
      const n = shape.length - 1;
      for (let i = 1; i <= n; i++) p.linearRampToValueAtTime(shape[i], t0 + dur * i / n);
    } catch (err) { console.warn("[gate]", err); }
  }
}

// seq(s, 0 | 1): a sound's sequence stopped after its note, and started again
const stoppedSeqs = new Set();

/** The sound's gate, from where it is at t, to `to` over `secs`. */
function rampGate(voice, to, t, secs) {
  const p = voice.graph?.gate?.gain;
  if (!p) return;
  try { holdAt(p, t); p.linearRampToValueAtTime(to, t + Math.max(0.005, secs)); } catch (err) { console.warn("[seq]", err); }
}

/**
 * Stopped at t: no next step. The note's own envelope (envPerStep) has
 * already ended it; a note held from step to step dies away over env's r.
 */
function parkVoice(voice, t) {
  voice._parked = true;
  if (voice._timer) { clearTimeout(voice._timer); voice._timer = null; }
  if (voice._ringing) return;                  // already dying away over the whole env's r
  const perStep = voice.params?.envPerStep ?? voice.params?.articulate;
  rampGate(voice, 0, t, perStep ? 0.01 : (voice.env?.r ?? 0.1));
}

/**
 * seq(s, 0): every voice of s finishes the note it is on and stops there --
 * its envelope dies away, no hard cut, no note left sustaining. With a whole
 * env: (beside envPerStep:) no note after it either: the one sounding is let
 * ring, not released, and dies away over env's r:. A sound that does not
 * step fades over its release.
 * seq(s, 1): on again, from the next beat (quant:), where its patterns
 * were; a whole env: swells in over its a:.
 */
export function seqSynth(name, on) {
  name = String(name);
  if (on) stoppedSeqs.delete(name); else stoppedSeqs.add(name);
  stateChanged();
  const ctx = sharedAudioCtx, now = ctx.currentTime;
  const gated = gatedNames.has(name);
  let at = null;
  for (const [k, v] of activeSynthVoices) {
    if (k !== name && !k.startsWith(`${name}__`)) continue;
    const env = v.env || { a: 0.02, r: 0.1 };
    if (!on) {
      if (v._seqStop || v._parked) continue;
      v._seqStop = true;
      if (v.wholeEnv) {
        // the note's own release not come yet: taken away, so it rings on
        // at its sustain under the fade (events still to come: no click)
        if (v._releaseAt > now + 0.02) { try { v.graph.gain.gain.cancelScheduledValues(v._releaseAt); } catch { /* ignore */ } }
        v._ringing = true;
        rampGate(v, 0, now + 0.01, v.wholeEnv.r);
      }
      if (!v._hasStepEngine) parkVoice(v, now + 0.01);
      continue;
    }
    if (!v._seqStop && !v._parked) continue;
    v._seqStop = false;
    v._ringing = false;
    const perStep = v.params?.envPerStep ?? v.params?.articulate;
    const rise = v.wholeEnv ? v.wholeEnv.a : perStep ? 0.005 : env.a;
    if (!v._parked) { if (!gated) rampGate(v, 1, now + 0.01, rise); continue; }   // still dying away: back up
    v._parked = false;
    if (!v._hasStepEngine) { if (!gated) rampGate(v, 1, now + 0.01, rise); continue; }
    // on the beat, every voice of it together
    if (at == null) {
      const q = Number(v.params?.quant);
      at = Number.isFinite(q) && q <= 0 ? now + 0.05 : quantStartSec(Number.isFinite(q) && q > 0 ? q : 1);
    }
    if (!gated) rampGate(v, 1, at, rise);
    v._nextSec = at;
    v._timer = setTimeout(() => {
      v._timer = null;
      if (v._stopped || v._parked || activeSynthVoices.get(v.uid) !== v) return;
      scheduleNextStep(v, false);
    }, Math.max(0, (at - ctx.currentTime) * 1000 - STEP_LEAD_MS));
  }
}

function stopMods(voice) {
  for (const m of voice?._mods?.values() || []) m.stop();
  voice?._mods?.clear();
  voice?.graph?.fuzz?.stop();
  voice?.graph?.sub?.stop();
  voice?.graph?.grit?.stop();
}

// ============================================================
// 🔌 Voice graph construction
// ============================================================

// Create source(s) - handles both single freq and chord arrays
function createSource(ctx, wave, freqParam, fm = null) {
  const w = String(wave ?? "sine").toLowerCase();

  // the external input (wave:in): a sound with no pitch, through the voice (sources.js)
  if (INPUT_WAVES.has(w)) {
    const node = inputSource(ctx, text => dispatchEvent(new CustomEvent("oscilla:livecode-say", { detail: { text: `[synth] ${text}`, kind: "warn" } })));
    trackOscillator(node);
    return { kind: "noise", node, wave: w, isChord: false, oscillators: null, input: true };
  }
  // dust and step: noises whose freq: is their rate (one rate: a list's first)
  if (GEN_WAVES.has(w)) {
    const node = genSource(ctx, w, pitchToHz(Array.isArray(freqParam) ? freqParam[0] : freqParam));
    if (node) {
      trackOscillator(node);
      return { kind: "osc", node, wave: w, isChord: false, oscillators: null, gen: true };
    }
  }

  // Check if freq is an array (chord)
  const isChord = Array.isArray(freqParam) && freqParam.length > 1;

  if (w === "noise" || w === "white" || w === "pink" || w === "brown" || GEN_WAVES.has(w)) {
    const colour = (w === "pink" || w === "brown") ? w : "white";
    const src = ctx.createBufferSource();
    // 8 s, so a loud wall of it does not audibly repeat
    src.buffer = getNoiseBuffer(ctx, 8, colour);
    src.loop = true;
    trackOscillator(src);  // Track for panic cleanup
    return { kind: "noise", node: src, wave: w, isChord: false, oscillators: null };
  }

  if (isChord) {
    // Create multiple oscillators for chord
    const oscillators = freqParam.map(freq => {
      const osc = ctx.createOscillator();
      osc.type = OSC_WAVES[w] ?? w;
      const hz = pitchToHz(freq);
      osc.frequency.value = clamp(hz, 1, 20000);
      trackOscillator(osc);  // Track for panic cleanup
      attachFM(ctx, osc, fm, trackOscillator);
      return osc;
    });

    // Create a mixer gain node to sum all oscillators
    const mixer = ctx.createGain();
    // Scale amplitude by number of voices to prevent clipping
    mixer.gain.value = 1.0 / Math.sqrt(oscillators.length);

    oscillators.forEach(osc => osc.connect(mixer));

    return {
      kind: "chord",
      node: mixer,  // The mixer is the "node" that connects to the rest of the graph
      wave: w,
      isChord: true,
      oscillators: oscillators,
      frequencies: freqParam.map(f => pitchToHz(f))
    };
  }

  // Single oscillator
  const osc = ctx.createOscillator();
  // saw is sawtooth: an oscillator ignores a type it does not know, and
  // every wave:saw was a sine
  osc.type = OSC_WAVES[w] ?? w;
  // Set initial frequency (freqParam could be number, string, or note name)
  const initialHz = pitchToHz(freqParam);
  osc.frequency.value = clamp(initialHz, 1, 20000);
  trackOscillator(osc);  // Track for panic cleanup
  attachFM(ctx, osc, fm, trackOscillator);
  return { kind: "osc", node: osc, wave: w, isChord: false, oscillators: null };
}

/** name -> { lo, hi }: the pitch range a synth has played, for its t channel */
const pitchRanges = new Map();

/** A step's pitch on the bus, under the synth's own name (see applyStepTargets). */
function publishPitch(voice, hz) {
  const name = String(voice.uid ?? "").split("__")[0];
  if (!name) return;
  const r = pitchRanges.get(name) || { lo: hz, hi: hz };
  r.lo = Math.min(r.lo, hz); r.hi = Math.max(r.hi, hz);
  pitchRanges.set(name, r);
  // in pitch, not Hz: each octave an equal share (in Hz the octaves above
  // crowded the ones below into the bottom of the range)
  const t = r.hi > r.lo ? Math.log(hz / r.lo) / Math.log(r.hi / r.lo) : 0;
  pulse(name, "synth", { freq: hz, amp: voice.amp ?? 0, t });   // and hit, n: a step
}

const NOISES = ["noise", "white", "pink", "brown"];
const OSC_WAVES = { sine: "sine", square: "square", sawtooth: "sawtooth", saw: "sawtooth", triangle: "triangle" };

/**
 * A wave pattern that has noise in it as well as oscillator shapes: the
 * oscillator and a noise source per colour, each through a gain, summed --
 * a step opens the one it drew (switchWave). Returns the node the graph
 * takes, or the source's own when there is nothing to switch.
 */
function waveSwitch(ctx, source, wavePattern) {
  const values = (wavePattern?.values || []).map(v => String(v).toLowerCase());
  const colours = [...new Set(values.filter(v => NOISES.includes(v)).map(v => (v === "pink" || v === "brown") ? v : "white"))];
  if (!colours.length || source.kind === "noise") return source.node;
  const sum = ctx.createGain();
  const oscGain = ctx.createGain();
  source.node.connect(oscGain).connect(sum);
  const noises = {};
  for (const c of colours) {
    const src = ctx.createBufferSource();
    src.buffer = getNoiseBuffer(ctx, 8, c);
    src.loop = true;
    trackOscillator(src);
    const g = ctx.createGain();
    g.gain.value = 0;
    src.connect(g).connect(sum);
    noises[c] = { src, gain: g };
  }
  source.switch = { oscGain, noises };
  return sum;
}

/** A step's wave: an oscillator shape, or (with a switch) a noise. */
function switchWave(voice, w, t) {
  const src = voice.source;
  if (!src || w == null) return;
  w = String(w).toLowerCase();
  const sw = src.switch;
  if (NOISES.includes(w)) {
    if (!sw) return;                     // no noise source on this voice
    const c = (w === "pink" || w === "brown") ? w : "white";
    sw.oscGain.gain.setValueAtTime(0, t);
    for (const [k, n] of Object.entries(sw.noises)) n.gain.gain.setValueAtTime(k === c ? 1 : 0, t);
    return;
  }
  const type = OSC_WAVES[w];
  if (!type) return;                     // drums are not a step's wave
  for (const osc of src.oscillators || [src.node]) { try { osc.type = type; } catch { /* noise node */ } }
  src.wave = w;
  if (sw) {
    sw.oscGain.gain.setValueAtTime(1, t);
    for (const n of Object.values(sw.noises)) n.gain.gain.setValueAtTime(0, t);
  }
}

/** fuzz:'s values, as a follow() may move them. */
const FUZZ_RANGES = { starve: [0, 1], gain: [1, 200], bias: [0, 1], tone: [200, 16000] };

/**
 * A setting that is a follow(), or an object with follow()s in it
 * (fuzz:{starve:follow(...), tone:2000}): each bound, `apply` given the whole
 * setting as it is now each time one moves. `whole`: the key a bare follow()
 * is (fuzz:follow(...) is its starve). The unbinds back.
 */
function bindInside(value, apply, ranges, whole) {
  const out = [];
  if (isBindable(value)) {
    const [lo, hi] = ranges[whole];
    const b = bindParam(value, v => apply({ [whole]: v }), { min: lo, max: hi, default: lo });
    if (b?.unbind) out.push(b.unbind);
    return out;
  }
  if (!value || typeof value !== "object") return out;
  const now = { ...value };
  for (const k of Object.keys(value)) {
    if (!isBindable(value[k]) || !ranges[k]) continue;
    const [lo, hi] = ranges[k];
    now[k] = Array.isArray(value[k].range) ? Number(value[k].range[0]) : lo;
    const b = bindParam(value[k], v => { now[k] = v; apply({ ...now }); }, { min: lo, max: hi, default: lo });
    if (b?.unbind) out.push(b.unbind);
  }
  return out;
}

/** drive: on a voice's shaper: its curve, or none (the sound as it is). */
function setDrive(shaper, drive) {
  const curve = driveCurve(drive);
  shaper.curve = curve;
  shaper.oversample = curve ? "4x" : "none";
}

function connectGraph(ctx, sourceNode, params) {
  // SAFETY: default amp low, hard cap
  const amp = clamp(params.amp ?? 0.08, 0, 0.25);

  const gain = ctx.createGain();
  gain.gain.value = 0;

  // drive: a soft clip before the envelope (synthTimbre.js)
  // fuzz: a starved fuzz pedal, after drive (fuzz.js)
  // fold: clip: ring: comb: crush: after the fuzz, before the envelope (grit.js)
  const grit = buildGrit(ctx, params);
  if (grit) grit.output.connect(gain);
  const afterFuzz = grit ? grit.input : gain;
  const fuzz = params.fuzz != null && Number(params.fuzz) !== 0 ? buildFuzz(ctx, isBindable(params.fuzz) ? 0.5 : params.fuzz) : null;
  if (fuzz) fuzz.output.connect(afterFuzz);
  const into = fuzz ? fuzz.input : afterFuzz;

  // sub: an octave pedal's sub, first of all (sub.js) -- then drive, fuzz, envelope
  const sub = params.sub != null && Number(params.sub) !== 0 ? buildSub(ctx, params.sub) : null;
  if (sub) { sourceNode.connect(sub.input); sourceNode = sub.output; }

  // drive's shaper is always there -- no curve (drive:0 or none) passes the
  // sound as it is -- so a line run again with drive: drives a playing voice
  const shaper = ctx.createWaveShaper();
  setDrive(shaper, params.drive);
  sourceNode.connect(shaper).connect(into);
  // where the source goes in: a chord made later (a step drawing a list)
  // goes in there too, through the sub, drive and fuzz
  const sourceInto = sub ? sub.input : shaper;

  let after = gain;

  // amp:sinOsc(...) -- a tremolo after the envelope, at 1 unless modulated
  const trem = ctx.createGain();
  after.connect(trem);
  after = trem;

  // gate(s, 0 | 1): closed, the sound fades out before the effects (they
  // ring on) and the voice runs on silent; opened, it comes back (gate.js)
  const gate = ctx.createGain();
  gate.gain.value = params._gated ? 0 : 1;
  after.connect(gate);
  after = gate;

  // where the voice leaves for its bus: azi: places it among the speakers
  // (pan: is then not needed), else it goes as two channels (speakers.js)
  const spat = hasSpatial(params) ? spatialPanner(ctx) : null;
  const exit = spat ? spat.input : toBus(ctx);
  (spat ? spat.output : exit).connect(channelFor(params._name, synthOutput));   // its name's fader (level.js)

  // Pan (optional)
  let panner = null;
  if (params.pan != null && !spat) {
    panner = new StereoPannerNode(ctx, { pan: clamp(resolveVal(params.pan, 0), -1, 1) });
    after.connect(panner);
    after = panner;
  }

  // Filter (optional)
  const filter = buildFilter(ctx, params.filter);
  if (filter) {
    after.connect(filter);
    after = filter;
  }

  // FX sum
  const sum = ctx.createGain();
  // Whole-voice fade-in: ramp the output sum node — the same node the stop
  // path fades — so the fade is orthogonal to the per-note envelope and
  // works under envPerStep articulation (where env.a applies per note).
  const fadeInSec = Number(params.fadeIn ?? params.fadein ?? 0);
  if (Number.isFinite(fadeInSec) && fadeInSec > 0) {
    const t0 = ctx.currentTime;
    sum.gain.setValueAtTime(0.0001, t0);
    sum.gain.linearRampToValueAtTime(1.0, t0 + fadeInSec);
  } else {
    sum.gain.value = 1.0;
  }

  // Analyser: parallel tap for oscilloscope display.
  // Taps post-filter, pre-FX so the scope shows the shaped tone
  // without delay/reverb muddying the trace -- and after where a stop
  // fades the voice (sum, or pre with a reverb), so the trace dies away
  // over the release as the sound does. (With a delay alone the stop
  // fades the echoes too, and so does the trace.)
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0;

  // Delay + Reverb use dry/wet blocks
  const delay = buildDelay(ctx, params.delay);
  const reverb = buildReverb(ctx, params.reverb);

  if (!delay && !reverb) {
    after.connect(sum);
    sum.connect(analyser);
    sum.connect(exit);
    return { amp, gain, trem, gate, panner, spat, exit, filter, delay, reverb, sum, analyser, shaper, fuzz, sub, grit, sourceInto };
  }

  // where a stop fades the sound: before the effects, so the room and the
  // echoes ring out what they already heard (stopSynth)
  const pre = ctx.createGain();
  after.connect(pre);
  after = pre;
  // the trace from where a stop fades it (stopSingleVoice): pre with a
  // reverb, else sum
  (reverb ? pre : sum).connect(analyser);

  // Effects wiring strategy:
  // - Each effect has its own dry/wet mix controlled by its dry and wet gain nodes
  // - When only one effect: use its dry/wet directly
  // - When both effects: chain them (delay -> reverb), each with its own mix
  // - No separate "dry" node needed - effects' dry paths handle this

  if (delay && reverb) {
    // Both effects: delay feeds into reverb
    after.connect(delay.dry);
    after.connect(delay.delay);
    delay.delay.connect(delay.wet);

    // Delay output (dry + wet) goes to reverb
    delay.dry.connect(reverb.dry);
    delay.dry.connect(reverb.input);
    delay.wet.connect(reverb.dry);
    delay.wet.connect(reverb.input);

    // Reverb output goes to sum
    reverb.dry.connect(sum);
    reverb.wet.connect(sum);
  } else if (delay) {
    // Only delay
    after.connect(delay.dry);
    after.connect(delay.delay);
    delay.delay.connect(delay.wet);
    delay.dry.connect(sum);
    delay.wet.connect(sum);
  } else if (reverb) {
    // Only reverb
    after.connect(reverb.dry);
    after.connect(reverb.input);
    reverb.dry.connect(sum);
    reverb.wet.connect(sum);
  }

  sum.connect(exit);
  return { amp, gain, trem, gate, panner, spat, exit, filter, delay, reverb, sum, analyser, shaper, pre, fuzz, sub, grit, sourceInto };
}

// ============================================================
// 🧭 Region lifetime checker (call from main tick)
// As audioImpulse.js checkImpulseRegions()
// ============================================================
export function checkSynthRegions() {
  if (!window.activeSynthVoices) return;

  const playX = playheadScreenX();
  if (playX == null) return;

  for (const voice of window.activeSynthVoices.values()) {
    if (!voice) continue;
    if (!voice._regionEl) continue;
    if (voice._stopped) continue;

    // Use parent group's bbox if element is inside a group (for SVG-authored extent lines)
    // But NOT for audio objects on the interaction layer
    let regionEl = voice._regionEl;
    const isGroupHost = regionEl.tagName?.toLowerCase() === 'g';
    const hasDslExtent = regionEl._dslExtentW > 0;
    const parent = regionEl.parentElement;
    if (!isGroupHost && !hasDslExtent && isAuthoredExtentGroup(parent, regionEl)) {
      // Parent is a group (not the main layer) - use its bounds for extent
      regionEl = parent;
    }

    const rect = regionEl.getBoundingClientRect();
    const containerRect = window.scoreContainer?.getBoundingClientRect();

    const rectLeft = rect.left - (containerRect?.left || 0);
    let rectRight = rect.right - (containerRect?.left || 0);


    // DSL extent:N — boundary computed live from the element's current bbox
    if (voice._regionEl._dslExtentW > 0) {
      const scale = voice._regionEl.parentElement?.getScreenCTM?.()?.a;
      if (scale) {
        rectRight = (voice._regionEl.getBoundingClientRect().left - (containerRect?.left || 0)) + voice._regionEl._dslExtentW * scale;
      }
    }

    const EPS = 1.5;
    const inside = playX >= (rectLeft - EPS) && playX <= (rectRight + EPS);

    if (inside) {
      voice._wasInsideOnce = true;
    }

    if (!inside && voice._wasInsideOnce) {
      stopSynth(voice.uid);
    }
  }
}

// ============================================================
// 🎚 Public API
// ============================================================
// How far ahead of its moment a step is scheduled on the audio clock:
// enough for a timer that fires late and for currentTime, which moves a
// buffer at a time (~11 ms); little enough to change course.
const STEP_LEAD_MS = 40;
// And a quantised start this much more, so the voice exists when its beat
// comes and its first note is scheduled exactly.
const QUANT_LEAD_MS = 20;

/**
 * The beat grid on the audio clock. Steps are counted in audio time (step
 * n+1 = step n + dur, sample-exact); the grid is laid onto that clock ONCE,
 * through this anchor, so every voice on this machine shares one exact
 * grid. (Converting from the system clock at every step jittered by the
 * audio clock's buffer -- 107, 128, 107 ms apart for 120 -- and drifted
 * where the two clocks run at different rates.) The anchor is taken as
 * heard (getOutputTimestamp), so a beat sounds when the shared clock says;
 * re-taken only if the grid's clock changes (local -> server, once), or
 * this machine's latency() does. That latency (outputLatency.js: what the
 * browser cannot know, Bluetooth, a PA) moves the grid earlier on the audio
 * clock, so a note is heard on the beat. A sound already going keeps the
 * grid it started on.
 */
let gridAnchor = null;              // { domain, ms (grid clock), sec (audio clock) }

function gridToAudio() {
  const perf = performance.now();
  const g = gridNow(perf);
  if (!gridAnchor || gridAnchor.domain !== g.domain) {
    gridAnchor = { domain: g.domain, ms: g.ms, sec: heardAudioTime(perf) - outputLatencyMs() / 1000 };
  }
  return gridAnchor;
}
if (typeof window !== "undefined" && typeof window.addEventListener === "function") window.addEventListener("oscilla:output-latency", () => { gridAnchor = null; });

/** The grid time (the server's clock) of audio time sec, or null off the server's clock. */
function gridMsOfAudio(sec) {
  const a = gridAnchor;
  if (!a || a.domain !== "server" || !Number.isFinite(sec)) return null;
  return Math.round((a.ms + (sec - a.sec) * 1000) * 10) / 10;
}

/** The audio time of the next quant boundary (phase beats on; on a step grid too), far enough ahead to schedule. */
export function quantStartSec(quantBeats, stepBeats = 0, phaseBeats = 0) {
  const ctx = sharedAudioCtx;
  const a = gridToAudio();
  const minSec = ctx.currentTime + (STEP_LEAD_MS + QUANT_LEAD_MS) / 1000;
  const minGridMs = a.ms + (minSec - a.sec) * 1000;
  const boundary = nextGridBoundary(minGridMs, quantBeats, 0, stepBeats, phaseBeats);
  return a.sec + (boundary - a.ms) / 1000;
}

/** Seconds that `beats` beats last from audio time t (the tempo, tempo()). */
export function beatsAt(t, beats) {
  const a = gridToAudio();
  return beatsToSec(a.ms + (t - a.sec) * 1000, beats);
}

/** Does this synth step (a pattern in freq/amp/dur/filter, a pattern:, a list of chords)? */
function hasSteps(p) {
  const pat = v => v && typeof v === "object" && !Array.isArray(v) && v.type === "pattern";
  return [p.freq, p.amp, p.dur, p.filter?.freq, p.filter?.cutoff, p.filter?.q, p.pattern, p.wavePattern, p.octave, p.transpose].some(pat)
    || (Array.isArray(p.freq) && p.freq.some(Array.isArray)) || !!p.envPerStep;
}

/** quant: in beats; a stepping synth from the live console defaults to 1. */
function quantFor(params, opts) {
  // quant:[2, 1] -- every 2 beats, 1 on (SuperCollider's [quant, phase]): its first
  const q = Number(Array.isArray(params.quant) ? params.quant[0] : params.quant);
  if (params.quant != null && Number.isFinite(q)) return Math.max(0, q);
  return opts?.live && hasSteps(params) ? 1 : 0;
}

/**
 * amp:*0.5, freq:*1.5, pan:+=0.2 -- from the voice of this name sounding
 * now (its oscillator's pitch for freq), or from the defaults (relative.js).
 */
function relativeToVoice(ast) {
  if (!hasRel(ast?.args)) return ast;
  const uid = String(ast.args.find(a => a.type === "uid")?.value ?? "");
  const v = activeSynthVoices.get(uid);
  const now = k => {
    if (k === "freq") return v?.source?.node?.frequency?.value ?? v?.source?.oscillators?.[0]?.frequency?.value;
    const x = Number(v?.params?.[k]);
    return Number.isFinite(x) ? x : undefined;
  };
  // a parameter the voice has as a pattern: the pattern kept, the factor added
  // to it -- freq:*1.5 on Pseq(55, 82, inf) plays it a fifth up from now on
  const args = ast.args.map(a => {
    const raw = v?.params?.[a.type];
    return isRel(a.value) && raw?.type === "pattern" ? { ...a, value: { ...raw, ops: [...(raw.ops || []), a.value] } } : a;
  });
  return { ...ast, args: resolveRelArgs(args, now, { amp: 0.08, pan: 0, freq: 440, cutoff: 1200, q: 0.707 }) };
}

/**
 * ~E2 | edo(19), [~E2, ~G2] | harm(55): a note or a chord through pitch
 * filters, settled as the line runs (a pattern carries its own, applied
 * each step: audioShared.js).
 */
function settlePipes(ast) {
  const settle = v => (v?.type === "pipe" ? pipeValue(v.source, compilePipe(v.pipe)) : v);
  if (Array.isArray(ast?.args) && ast.args.some(a => a?.value?.type === "pipe")) {
    return { ...ast, args: ast.args.map(a => (a?.value?.type === "pipe" ? { ...a, value: settle(a.value) } : a)) };
  }
  if (ast?.params && Object.values(ast.params).some(v => v?.type === "pipe")) {
    return { ...ast, params: Object.fromEntries(Object.entries(ast.params).map(([k, v]) => [k, settle(v)])) };
  }
  return ast;
}

export async function handleSynthCue(ast, cueElement = null, opts = {}) {
  ast = settlePipes(relativeToVoice(ast));
  const ctx = sharedAudioCtx;
  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch {
      /* ignore */
    }
  }

  const params = extractParams(ast);
  let uid = String(params.uid ?? "").trim();

  // Auto-generate uid if not provided - must be stable for toggle behavior
  if (!uid) {
    if (cueElement?.id) {
      // Use a hash of the element id for stability (SVG-authored cues)
      uid = `synth-${cueElement.id.substring(0, 50).replace(/[^a-zA-Z0-9]/g, '_')}`;
    } else {
      // Fallback: use a random but store it on the element for stability
      if (!cueElement?._autoSynthUid) {
        const autoUid = `synth-auto-${Math.random().toString(36).slice(2, 10)}`;
        if (cueElement) cueElement._autoSynthUid = autoUid;
        uid = autoUid;
      } else {
        uid = cueElement._autoSynthUid;
      }
    }
  }

  if (opts.source && !opts._stepStartSec) soundLines.set(uid, String(opts.source));
  // bus:room -- through a shared fx bus, every voice of it (fxBus.js); bus:none its own way
  if (params.bus != null && !opts._stepStartSec) sendTo(uid, params.bus);
  // send:{bus:room, amount:0.5, chance:1/6} -- a copy into a bus as well, the sound
  // itself going its own way (fxBus.js sendCopy); with chance:, open only on the
  // steps the dice allow (applyStepTargets); send:room the whole of it; send:none none
  // with every:6, only each 6th step (every:peuclid(3, 8) those it gives a 1),
  // counted from this run, and chance: drawn only for those
  if (params.send != null && !opts._stepStartSec) {
    const sp = params.send && typeof params.send === "object" && !Array.isArray(params.send) ? params.send : { bus: params.send };
    const amount = Number.isFinite(Number(sp.amount)) ? Number(sp.amount) : 1;
    const chance = sp.chance != null && Number.isFinite(Number(sp.chance)) ? Math.max(0, Math.min(1, Number(sp.chance))) : null;
    const every = makeEvery(sp.every);
    const gain = sendCopy(uid, sp.bus, amount);
    if (gain) {
      sendsOf.set(String(uid).split("__")[0], { gain, amount, chance, every });
      // no steps of its own: drawn once, now
      if (chance != null || every) gain.gain.value = sendOpen({ chance, every }) ? amount : 0;
    } else sendsOf.delete(String(uid).split("__")[0]);
  }
  // vu:stereo / vu:mono -- how its meter shows it (vuModes.js)
  if (params.vu != null) setVuMode(uid, params.vu);
  // its line run again while seq(s, 0) has it stopped: it goes on (from the beat)
  if (opts.live && !opts._stepStartSec && stoppedSeqs.has(uid)) seqSynth(uid, true);

  // Ctrl+Alt+Enter on a sound that is not poly: as Ctrl+Enter, the line
  // rolled once (opts.reroll)
  if (opts.allVoices && opts.reroll && !(Math.floor(resolveVal(params.poly, 0)) > 0)) {
    return handleSynthCue(opts.reroll(), cueElement, { ...opts, allVoices: false, reroll: null });
  }

  // quant: -- start on the beat (beatGrid.js). A pattern run from the live
  // console starts on the next beat unless told otherwise (quant:0); one
  // the playhead fires starts where the playhead is. The wait is here, not
  // in the voice: a line run again replaces its voice on the beat too.
  const quant = quantFor(params, opts);
  if (quant > 0 && opts._stepStartSec == null) {
    // a fixed dur: on its step grid too, so every run of it steps together
    const d = Number(params.dur);
    const phase = Array.isArray(params.quant) ? Number(params.quant[1]) || 0 : 0;
    const at = quantStartSec(quant, typeof params.dur !== "object" && d > 0 ? d : 0, phase);
    const wait = (at - ctx.currentTime) * 1000 - STEP_LEAD_MS - QUANT_LEAD_MS;
    const go = () => handleSynthCue(ast, cueElement, { ...opts, _stepStartSec: at });
    if (wait > 1) {
      return new Promise(res => {
        const set = pendingStarts.get(uid) || pendingStarts.set(uid, new Set()).get(uid);
        const p = { done: res, timer: setTimeout(() => { set.delete(p); res(go()); }, wait) };
        set.add(p);
      });
    }
    return go();
  }

  // -----------------------------------------------------------
  // Polyphony: poly:N allows N overlapping voices per uid.
  // Each trigger creates a new sub-voice; oldest voice is
  // stolen when count exceeds N.
  // -----------------------------------------------------------
  const polyMax = Math.floor(resolveVal(params.poly, 0));

  if (polyMax > 0) {
    const baseUid = uid;

    // Toggle support for poly voices: if this is a click trigger and
    // toggle is enabled and voices are already playing, stop them all.
    const isClickTrigger = cueElement?._clickTriggered === true;
    if (polyGroups.has(baseUid) && togglesOff(params, isClickTrigger)) {
      const group = polyGroups.get(baseUid);
      if (group && group.subUids.length > 0) {
        stopSynth(baseUid);
        return null;
      }
    }

    const subUid = `${baseUid}__${polyCounter++}`;

    // Initialise or update group
    if (!polyGroups.has(baseUid)) {
      polyGroups.set(baseUid, { max: polyMax, subUids: [], liveRectUid: null });
    }
    const group = polyGroups.get(baseUid);
    group.max = polyMax;

    // Ctrl+Alt+Enter in the console: the line for every voice sounding.
    // Stepping voices take it in place, each on its own step (its next
    // notes have their envelopes). Held ones -- a chord -- each release on
    // their r: as as many new ones come in on the line's a:, each rolling
    // its own rand(). With none sounding, it starts as usual.
    if (opts.allVoices) {
      const playing = group.subUids.filter(s => activeSynthVoices.get(s) && !activeSynthVoices.get(s)._stopped);
      // opts.reroll: the line with its rand()s rolled afresh, one per voice
      const lineFor = () => opts.reroll?.() || ast;
      if (playing.length && hasSteps(params)) {
        for (const sub of playing) updateSynthVoice(sub, lineFor(), cueElement, opts);
        return activeSynthVoices.get(playing.at(-1));
      }
      if (playing.length) {
        for (const sub of playing) {
          group.subUids.splice(group.subUids.indexOf(sub), 1);
          stopSingleVoice(sub);
        }
        const fresh = [];
        for (let i = 0; i < playing.length; i++) fresh.push(handleSynthCue(lineFor(), cueElement, { ...opts, allVoices: false, reroll: null }));
        return (await Promise.all(fresh)).at(-1);
      }
      // none sounding: it starts as a Ctrl+Enter would
      if (opts.reroll) return handleSynthCue(lineFor(), cueElement, { ...opts, allVoices: false, reroll: null });
    }

    // Voice steal: stop oldest if at capacity
    while (group.subUids.length >= group.max) {
      const oldest = group.subUids.shift();
      stopSynth(oldest);
    }

    group.subUids.push(subUid);

    // Scope stacking: use a stable rect id based on the base uid
    // so that even after all voices finish and the group is recreated,
    // createLiveAudioRect reuses the existing rect (it dedupes by id).
    const polyOpts = { ...opts };
    const stableRectUid = `scope-${baseUid}`;
    if (!group.liveRectUid) {
      group.liveRectUid = stableRectUid;
    }
    // All voices (including the first) target the stable rect
    polyOpts._polyLiveRectUid = group.liveRectUid;
    polyOpts._polyBaseUid = baseUid;

    return startSynthVoice(subUid, ast, cueElement, polyOpts);
  }

  // -----------------------------------------------------------
  // Mono mode (default): toggle, update, or start voice
  // -----------------------------------------------------------
  if (activeSynthVoices.has(uid)) {
    const existing = activeSynthVoices.get(uid);
    const isClickTrigger = cueElement?._clickTriggered === true;

    if (!existing?._stopped && togglesOff(params, isClickTrigger)) {
      stopSynth(uid);
      return null;
    }
    if (existing?._stopped || !isClickTrigger) {
      // Stopped voice or playhead re-entry: quick-kill and start fresh
      stopSingleVoice(uid, 0.005);
    } else {
      // Click trigger on active voice, toggle:false: update it
      return updateSynthVoice(uid, ast, cueElement, opts);
    }
  }

  return startSynthVoice(uid, ast, cueElement, opts);
}

/**
 * Whether running a playing synth's cue stops it. A click (on the synth,
 * or a button() that plays it) does, unless toggle:false; anything else
 * (the playhead, the console) only with toggle:true.
 */
function togglesOff(params, isClick) {
  const t = params.toggle;
  if (t === false || t === 0 || t === "0" || t === "false") return false;
  return isClick || t === true || t === 1 || t === "1" || t === "true";
}

// Track ALL created oscillators globally for nuclear cleanup
const allOscillators = window._allSynthOscillators || (window._allSynthOscillators = new Set());

/**
 * Register a source for hush()'s panic stop, until it ends: a node that
 * has stopped is let go, or every step of an overlap: line (one source a
 * step) stayed held until the next hush().
 */
function trackOscillator(osc) {
  allOscillators.add(osc);
  osc.addEventListener?.("ended", () => allOscillators.delete(osc), { once: true });
}

/**
 * Emergency stop all synths - use as panic button
 * Immediately disconnects all audio nodes and clears all state
 */
export function stopAllSynths() {
  console.log("[synth] PANIC: Stopping all synths");
  cancelPendingStarts();            // and none waiting for its beat starts after

  const ctx = sharedAudioCtx;
  const now = ctx.currentTime;

  // Immediately silence and clean up all voices -- and those stepping on under a fade out
  for (const voice of [...activeSynthVoices.values(), ...fadingVoices]) {
    voice._fadeEnd = 0;
    stopMods(voice);
    // its scope stops drawing too (a console box's goes with the box)
    if (voice._scopeHandle) { try { stopScope(voice._scopeHandle); } catch { /* ignore */ } }

    // Cancel envelope and set gain to 0 immediately
    try {
      voice.graph?.gain?.gain?.cancelScheduledValues(now);
      voice.graph?.gain?.gain?.setValueAtTime(0, now);
    } catch { /* ignore */ }

    // Disconnect entire audio graph including FX feedback loops
    try { voice.graph?.sum?.disconnect(); } catch { /* ignore */ }
    voice.graph?.spat?.dispose();
    try { voice.graph?.exit?.disconnect(); } catch { /* ignore */ }
    try { voice.graph?.gain?.disconnect(); } catch { /* ignore */ }
    try { voice.graph?.delay?.delay?.disconnect(); } catch { /* ignore */ }
    try { voice.graph?.delay?.fbGain?.disconnect(); } catch { /* ignore */ }
    disconnectReverb(voice.graph?.reverb);

    // Mark stopped and clear timers
    voice._stopped = true;
    if (voice._timer) clearTimeout(voice._timer);
    if (voice._stopTimeout) clearTimeout(voice._stopTimeout);
  }

  polyGroups.clear();
  fadingVoices.clear();

  // Kill all tracked oscillators
  for (const osc of allOscillators) {
    try { osc.disconnect(); } catch { /* ignore */ }
    try { osc.stop(); } catch { /* ignore */ }
  }
  allOscillators.clear();

  activeSynthVoices.clear();
}

/**
 * NUCLEAR OPTION: Suspend audio context to kill ALL sound
 * Use when stopAllSynths doesn't work
 */
export async function nukeSynthAudio() {
  stopAllSynths();

  // Suspend audio context briefly to kill any residual audio
  try {
    await sharedAudioCtx.suspend();
    setTimeout(() => sharedAudioCtx.resume().catch(() => { /* context closed */ }), 100);
  } catch { /* ignore */ }
}

// Expose globally for emergency use
window.stopAllSynths = stopAllSynths;

/** once: written as on: 1, true, "1" (0 / off / false: not). */
const isOnce = v => v != null && !["0", "false", "off", "no"].includes(String(v).toLowerCase());

export function stopSynth(uidOrAst, rel = null) {
  const uid = typeof uidOrAst === "string" ? uidOrAst : String(extractParams(uidOrAst).uid ?? "").trim();
  if (!uid) return;
  cancelPendingStarts(uid);         // one waiting for its beat does not start after

  // If uid is a poly group base, stop all sub-voices
  const group = polyGroups.get(uid);
  if (group) {
    const subUids = [...group.subUids];
    group.subUids.length = 0;
    polyGroups.delete(uid);
    for (const sub of subUids) {
      stopSingleVoice(sub, rel);
    }
    // Also stop a mono voice with the same uid if one exists
    if (activeSynthVoices.has(uid)) {
      stopSingleVoice(uid, rel);
    }
    return;
  }

  stopSingleVoice(uid, rel);
}

function stopSingleVoice(uid, rel = null) {
  const voice = activeSynthVoices.get(uid);
  if (!voice) return;
  if (voice._stopped) return;

  voice._stopped = true;
  activeSynthVoices.delete(uid);
  waitTag(uid);
  unregisterAnalyser(uid, voice.graph?.analyser);

  const ctx = sharedAudioCtx;
  const now = nowSec(ctx);

  // Unbind parameter subscriptions immediately to prevent gain resets
  if (voice._unbinders) {
    voice._unbinders.forEach(unbind => unbind());
    voice._unbinders = [];
  }

  // Audio teardown: smooth release → stop sources → cleanup graph
  //
  // IMPORTANT: We do NOT cancel or modify the main voice gain automation.
  // cancelScheduledValues / cancelAndHoldAtTime on a gain node with active
  // envPerStep automation can produce a single-sample discontinuity.
  // With delay effects (especially high feedback), that micro-glitch gets
  // echoed dozens of times and becomes an audible click train.
  //
  // Instead we fade the OUTPUT sum node, which has no existing automation
  // (static 1.0), so scheduling on it is glitch-free.  The main gain's
  // current envPerStep ADSR continues undisturbed — its signal just gets
  // multiplied by a smoothly-declining sum gain on the way to destination.
  // The release: rel: given, else the sound's own -- with env: beside
  // envPerStep:, env:'s (the whole sound's), not the 0.01 each note ends on
  const env = voice.env || { a: 0.02, d: 0, s: 1, r: 0.1 };
  const relSec = rel != null ? Number(rel) : (Number(voice.relOverride) || voice.wholeEnv?.r || env.r);
  const R = Math.max(0.003, Number(relSec) || 0.1);

  // A stepping sound faded out over a release of its own plays on under the
  // fade (its pattern going on, fainter) until it is silent -- stopped at
  // once, its last note was gone within a step and the fade was over
  // nothing. A short one stops stepping now.
  if (voice._hasStepEngine && !voice._oneShot && R > FADE_STEPPING) { voice._fadeEnd = now + R; fadingVoices.add(voice); }
  else if (voice._timer) { clearTimeout(voice._timer); voice._timer = null; }
  if (voice._stopTimeout) { clearTimeout(voice._stopTimeout); voice._stopTimeout = null; }

  // Visual teardown at the end of the release, not its start: the trace
  // fades as the sound does. (Not if a new voice has taken the scope on
  // meanwhile -- a mono sound run again draws on the same one.)
  const handle = voice._scopeHandle;
  if (handle) {
    fadeScope(handle, R);            // fading out with the sound
    setTimeout(() => {
      for (const v of activeSynthVoices.values()) if (v._scopeHandle === handle) return;
      stopScope(handle);
      handle._stopExtentAnimation?.();
    }, R * 1000);
  }

  // Fade output sum to silence — no cancel needed, sum.gain is clean.
  // With a reverb, the fade is before the effects (graph.pre, as clean):
  // the room rings out after the note, as a room does.
  const fadeNode = voice.graph.reverb && voice.graph.pre ? voice.graph.pre : voice.graph.sum;
  try {
    fadeNode.gain.setValueAtTime(fadeNode.gain.value, now);
    fadeNode.gain.linearRampToValueAtTime(0, now + R);
  } catch { /* ignore */ }

  // Kill delay/reverb feedback loops so echoes decay during the fade
  let fxTail = 0;
  if (voice.graph.delay) {
    const delayTime = voice.graph.delay.delay?.delayTime?.value || 0.25;
    const fb = voice.graph.delay.fbGain?.gain?.value ?? 0;
    // Taps until feedback decays to -60 dB: fb^n = 0.001 (one running away
    // dies as fast as 0.9 once its feedback is ramped down)
    const fbDecay = Math.min(fb, 0.9);
    const taps = fbDecay > 0 ? Math.ceil(Math.log(0.001) / Math.log(fbDecay)) : 0;
    fxTail = Math.min(10, taps * delayTime);  // cap at 10s
    try {
      // Ramp feedback to 0 over the release so no new echoes form
      voice.graph.delay.fbGain.gain.setValueAtTime(fb, now);
      voice.graph.delay.fbGain.gain.linearRampToValueAtTime(0, now + R);
    } catch { /* ignore */ }
  }
  if (voice.graph.reverb) fxTail = Math.max(fxTail, voice.graph.reverb.time);

  // Schedule source stop after the output fade completes
  const stopTime = now + R + 0.05;
  try {
    if (voice.source?.isChord && voice.source?.oscillators) {
      voice.source.oscillators.forEach(osc => { try { osc.stop(stopTime); } catch { /* already stopped */ } });
    } else {
      voice.source?.node?.stop?.(stopTime);
    }
    for (const n of Object.values(voice.source?.switch?.noises || {})) { try { n.src.stop(stopTime); } catch { /* already stopped */ } }
    for (const nt of voice.lanes?.notes || []) { try { nt.source.node.stop(stopTime); } catch { /* already stopped */ } }
  } catch { /* ignore */ }

  // Cleanup after release + FX tail have fully decayed
  // (at most 15 s on -- but never before a fade still playing has ended)
  const cleanupDelay = R + fxTail + 0.25;
  setTimeout(() => cleanupVoice(uid, voice),
    Math.min(Math.max(15, R + 0.3), cleanupDelay) * 1000);

  if (isOscEnabled(voice.ast, voice.params)) {
    sendOSCSynth(voice.ast, voice.params, { state: "stop", rel: R });
  }
}

// ============================================================
//  Start / Update
// ============================================================

function startSynthVoice(uid, ast, cueElement, opts) {
  const ctx = sharedAudioCtx;
  const params = extractParams(ast);

  // ========== CONTROL PLANE: Unbinder collection ==========
  const unbinders = [];
  // =========================================================

  // Non-bindable parameters (static)
  const wave = params.wave ?? "sine";
  const env = normaliseEnv(params.env);
  
  // lifetime handling
  // If explicit duration is set, use "process" so the duration timeout controls stopping
  // (region lifetime would stop early when playhead exits the bbox)
  let lifetime = params.lifetime ?? params.life ?? null;
  if (!lifetime) {
    // Check if dur is a number > 0, OR if dur is a pattern object (like Pxrand, Pseq, etc.)
    // Also check for envPerStep which implies step-based operation
    const durVal = params.dur;
    const hasDuration = durVal != null && (
      (typeof durVal === "number" && durVal > 0) ||
      (typeof durVal === "string" && Number(durVal) > 0) ||
      (typeof durVal === "object" && durVal.type === "pattern")  // Pattern objects count as having duration
    );
    const hasStepEngine = Boolean(params.envPerStep) || Boolean(params.articulate);
    lifetime = (hasDuration || hasStepEngine) ? "process" : (cueElement ? "region" : "process");
  }
  lifetime = String(lifetime).toLowerCase();

  // Create source (wave type is static)
  let freqParamRaw = params.freq ?? 440;
  // stack:[0, 7] on a held note (or chord): each note with these above it
  const stepping = freqParamRaw?.type === "pattern" || (Array.isArray(freqParamRaw) && freqParamRaw.some(Array.isArray));
  if (params.stack != null && !stepping && !isSignalRef(freqParamRaw) && !isModulator(freqParamRaw)) {
    freqParamRaw = stackNotes(freqParamRaw, params.stack);
  }
  // octave:-1 on a held note (or chord): the lot an octave down
  if (params.octave != null && params.octave?.type !== "pattern" && !stepping && !isSignalRef(freqParamRaw) && !isModulator(freqParamRaw)) {
    freqParamRaw = shiftOctaves(freqParamRaw, params.octave);
  }
  // transpose:5 on a held note (or chord): the lot a fourth up
  if (params.transpose != null && params.transpose?.type !== "pattern" && !stepping && !isSignalRef(freqParamRaw) && !isModulator(freqParamRaw)) {
    freqParamRaw = shiftSemitones(freqParamRaw, Number(evalMaybeRandom(params.transpose)) || 0);
  }

  // For chord arrays, don't bind (static)
  const isChord = Array.isArray(freqParamRaw) && freqParamRaw.length > 1;

  // Get initial frequency for source creation
  let initialFreq = 440;
  const freqMod = isModulator(freqParamRaw);      // freq:sinOsc(...): the modulator is all of it
  if (!isSignalRef(freqParamRaw) && !freqMod) {
    initialFreq = isChord ? freqParamRaw : pitchToHz(freqParamRaw);
  }
  
  // a wave pattern of noises only starts as a noise; with shapes in it, an oscillator
  const firstOsc = params.wavePattern ? (params.wavePattern.values || []).map(v => String(v).toLowerCase()).find(v => OSC_WAVES[v]) : null;
  const source = createSource(ctx, firstOsc ?? wave, isChord ? freqParamRaw : initialFreq, fmSpec(params.fm));
  const sourceOut = params.wavePattern ? waveSwitch(ctx, source, params.wavePattern) : source.node;
  if (source.switch && NOISES.includes(String(wave).toLowerCase())) switchWave({ source }, wave, nowSec(ctx));

  // Graph (amp will be bound after). amp:sinOsc(..., lo, hi): the envelope
  // peaks at hi, and the tremolo after it moves between lo/hi and 1
  const ampSpec = isModulator(params.amp) ? modSpec(params.amp) : null;
  const ampTop = ampSpec ? Math.max(ampSpec.lo, ampSpec.hi, 1e-6) : 0;
  const initialAmp = ampSpec ? clamp(ampTop, 0, 0.25)
    : isSignalRef(params.amp) ? 0.08 : clamp(resolveVal(params.amp, 0.08), 0, 0.25);
  const graph = connectGraph(ctx, sourceOut, {
    ...params,
    amp: initialAmp,
    _name: uid,
    _gated: gatedNames.has(String(uid).split("__")[0]),
  });

  // fold: ... crush:, drive: and fuzz: following a signal: the sound changes as it moves
  if (graph.grit) unbinders.push(...graph.grit.bind());
  if (isBindable(params.drive)) {
    const b = bindParam(params.drive, v => setDrive(graph.shaper, v), { min: 0, max: 1, default: 0 });
    unbinders.push(b.unbind);
  }
  if (graph.fuzz) unbinders.push(...bindInside(params.fuzz, cfg => graph.fuzz.set(cfg), FUZZ_RANGES, "starve"));

  // overlap:N -- each step a note of its own (an oscillator and its
  // envelope), into the same sub / drive / fuzz / envelope / effects: a
  // note's release rings on under the next ones, up to N at once
  // (applyStepTargets). The voice's own envelope node stays open.
  const overlapN = Math.max(0, Math.min(16, Math.round(Number(params.overlap) || 0)));
  let lanes = null;
  if (overlapN >= 2 && (params.envPerStep || params.articulate) && source.kind === "osc" && !source.gen && !isChord && !params.wavePattern) {
    try { source.node.disconnect(); } catch { /* not connected */ }
    lanes = { n: overlapN, notes: [], wave, into: graph.sourceInto };
  }

  // Start time: now -- or, quantised, the beat's own moment on the audio
  // clock. The voice is made ahead of its beat (STEP_LEAD_MS) so it is
  // ready; begun at once it sounded that much early, and by however late
  // the timer fired: the first note of each voice off the grid.
  const t0 = opts?._stepStartSec != null ? Math.max(opts._stepStartSec, nowSec(ctx)) : nowSec(ctx);
  // env: beside envPerStep: -- the whole sound swells in
  const whole = params.wholeEnv ? normaliseEnv(params.wholeEnv) : null;
  if (whole?.a > 0.005 && !gatedNames.has(String(uid).split("__")[0])) {
    try { graph.gate.gain.setValueAtTime(0, t0); graph.gate.gain.linearRampToValueAtTime(1, t0 + whole.a); } catch { /* ignore */ }
  }
  
  // ========== FREQUENCY BINDING ==========
  if (!isChord && source.kind === "osc" && !freqMod) {
    // For static values (not signal refs), pass the converted Hz value
    // For signal refs, pass the raw signalRef object
    const freqForBinding = isSignalRef(freqParamRaw) ? freqParamRaw : initialFreq;
    const freqBinding = bindParam(
      freqForBinding,
      (hz) => {
        try {
          source.node.frequency.setTargetAtTime(
            clamp(hz, 20, 20000),
            ctx.currentTime,
            0.02  // glide time
          );
        } catch (e) { /* ignore */ }
      },
      { min: 20, max: 20000, default: 440, audio: true }
    );
    unbinders.push(freqBinding.unbind);
    
    // Set initial frequency
    try {
      source.node.frequency.setValueAtTime(
        clamp(freqBinding.value, 20, 20000),
        t0
      );
    } catch (e) { /* ignore */ }
  }
  // ========================================

  // ========== AMPLITUDE BINDING ==========
  const ampBinding = bindParam(
    params.amp,
    (amp) => {
      try {
        const safeAmp = clamp(amp, 0, 0.5);
        graph.gain.gain.setTargetAtTime(safeAmp, ctx.currentTime, 0.02);
      } catch (e) { /* ignore */ }
    },
    { min: 0, max: 0.5, default: 0.08, audio: true }
  );
  unbinders.push(ampBinding.unbind);
  
  const amp = ampSpec ? initialAmp : clamp(ampBinding.value, 0, 0.25);
  // ========================================

  // ========== PAN BINDING ==========
  if (graph.panner) {
    const panBinding = bindParam(
      params.pan,
      (pan) => {
        try {
          graph.panner.pan.setTargetAtTime(
            clamp(pan, -1, 1),
            ctx.currentTime,
            0.02
          );
        } catch (e) { /* ignore */ }
      },
      { min: -1, max: 1, default: 0, audio: true }
    );
    unbinders.push(panBinding.unbind);
  }
  // azi:, elev:, dist: -- a number, a follow(), a sinOsc() (a pattern steps, runStep)
  const spatUnbinds = graph.spat ? bindSpatial(graph.spat, params) : [];
  unbinders.push(...spatUnbinds);
  // ==================================

  // ========== FILTER BINDINGS ==========
  if (graph.filter) {
    // Cutoff frequency
    // filter:{freq:follow(...)} is the documented form; cutoff: / filterFreq:
    // the older top-level ones. Bound live either way.
    const cutoffBinding = bindParam(
      params.cutoff ?? params.filterFreq
        ?? (isSignalRef(params.filter?.freq) ? params.filter.freq : null)
        ?? (isSignalRef(params.filter?.cutoff) ? params.filter.cutoff : null),
      (freq) => {
        try {
          graph.filter.frequency.setTargetAtTime(
            clamp(freq, 20, 20000),
            ctx.currentTime,
            0.02
          );
        } catch (e) { /* ignore */ }
      },
      { min: 20, max: 20000, default: 1000, audio: true }
    );
    unbinders.push(cutoffBinding.unbind);

    // Q / Resonance
    const qBinding = bindParam(
      params.q ?? params.resonance ?? (isSignalRef(params.filter?.q) ? params.filter.q : null),
      (q) => {
        try {
          graph.filter.Q.setTargetAtTime(
            clamp(q, 0.1, 30),
            ctx.currentTime,
            0.02
          );
        } catch (e) { /* ignore */ }
      },
      { min: 0.1, max: 30, default: 1, audio: true }
    );
    unbinders.push(qBinding.unbind);
  }
  // =====================================

  // Apply envelope with bound amp value
  applyADSR(graph.gain.gain, ctx, t0, amp, env);

  // overlap: the notes are in the lanes -- the voice's own envelope stays open
  if (lanes) {
    try { graph.gain.gain.cancelScheduledValues(t0); graph.gain.gain.setValueAtTime(1, t0); } catch { /* ignore */ }
  }

  // Start source(s)
  try {
    if (source.isChord && source.oscillators) {
      source.oscillators.forEach(osc => osc.start(t0));
    } else {
      source.node.start(t0);
    }
    for (const n of Object.values(source.switch?.noises || {})) n.src.start(t0);
  } catch {
    /* ignore */
  }
  // a held note is struck once (FM decay:); a stepping one at every step
  for (const o of source.oscillators || (source.kind === "osc" ? [source.node] : [])) fmStrike(o, t0);

  // (Synth display is handled by the SVG scope in renderScope/primeSynthScope)

  const voice = {
    uid,
    ast,
    params,
    // once:1 -- one step, then let go (a drum struck by a trig: hit, once a hit)
    _oneShot: isOnce(params.once),
    source,
    lanes,
    graph,
    env,
    wholeEnv: whole,
    _stepStartSec: opts?._stepStartSec ?? null,   // a quantised start: its beat on the audio clock
    amp,
    lifetime,
    _regionEl: cueElement || null,
    _wasInsideOnce: false,
    _timer: null,
    _stopTimeout: null,
    _stopped: false,
    relOverride: params.rel ?? params.release ?? params.fadeOut ?? params.fadeout ?? null,
    _scopeHandle: null, // oscilloscope display handle
    
    // ========== CONTROL PLANE: Store unbinders ==========
    _unbinders: unbinders,
    _spatUnbinds: spatUnbinds,
    // =====================================================

    // pattern generators
    _freqGen: null,
    _ampGen: null,
    _cutoffGen: null,
    _qGen: null,
    _durGen: null,
    _synthConfigGen: null,  // for patterns of complete synth configs
    _currentStepConfig: null,  // current step's synthConfig args (if using pattern of synth configs)
    _hasStepEngine: false,
    // glide: (portamento, beats): notes struck one by one (envPerStep) do
    // not slide into each other unless asked; legato lines glide a little
    glide: clamp(params.glide ?? (params.envPerStep || params.articulate ? 0 : 0.02), 0, 10),
    interp: String(params.interp ?? "smooth").toLowerCase()
  };

  voice._wasInsideOnce = false;

  voice._hold = heldNames.has(String(uid).split("__")[0]);
  voice._seqStop = false;
  voice._parked = false;

  // sinOsc() and family on its parameters: each voice its own (rand() drawn per voice)
  syncMod(voice, "freq", !isChord && source.kind === "osc" ? source.node.frequency : null, freqParamRaw);
  syncMod(voice, "amp", graph.trem.gain, params.amp, { spec: ampSpec, scale: 1 / (ampTop || 1) });
  syncMod(voice, "pan", graph.panner?.pan, params.pan);
  syncMod(voice, "cutoff", graph.filter?.frequency, params.filter?.freq ?? params.filter?.cutoff);
  syncMod(voice, "q", graph.filter?.Q, params.filter?.q);

  activeSynthVoices.set(uid, voice);
  waitTag(uid);
  registerAnalyser(uid, voice.graph?.analyser);   // viz(other, view:xy, y:<uid>)

  // ---------------------------------------------------------
  // OSCILLOSCOPE DISPLAY
  //
  // Default: "self" (render into the cue element).
  // Use waveform:none to suppress.
  // Taps the AnalyserNode in the signal chain for real-time
  // time-domain display.
  //
  // When cueElement is present (playhead trigger): render scope
  // into the cue element's bbox.
  //
  // When cueElement is null (live console restart): look for an
  // existing scope handle from a previous trigger and reconnect
  // it to the new voice's analyser.
  // ---------------------------------------------------------
  const waveformParam = params.waveform ?? "self";
  if (waveformParam !== "none" && waveformParam !== "0" && graph.analyser) {
    const scopeUid = `scope-${uid}`;

    if (cueElement) {
      // Normal path: render scope into cue element
      const svg = cueElement.ownerSVGElement;
      if (svg) {
        // Detailed info label for the SVG scope text
        const scopeInfo = formatScopeInfo(params, wave, {
          freqHz: (!source.isChord && source.kind === "osc")
            ? source.node?.frequency?.value : null,
          amp: amp,
          playbackClients: getPlaybackClientsFromEl(cueElement)
        });

        const scopeHandle = renderScope(svg, waveformParam, scopeUid, {
          element: cueElement,
          info: scopeInfo,
          wave: wave,
          color: getSynthTypeColor(params),
          backgroundColor: cueElement?.dataset?.playbackBackground || null
        });

        if (scopeHandle) {
          voice._scopeHandle = scopeHandle;
          scopeHandle._scopeWave = wave; // update in case wave changed
          startScope(scopeHandle, graph.analyser);
          // Start extent line animation
          if (scopeHandle._startExtentAnimation) {
            scopeHandle._startExtentAnimation();
          }

          if (scopeHandle._infoText) {
            setInfoOpacity(scopeHandle._infoText, "1");
          }
        }
      }
    } else {
      // Live console path: reconnect existing scope, or create
      // ephemeral live rect for first-time live-coded synth voices
      const existingScope = getWaveform(scopeUid);
      if (existingScope?._scopeLine) {
        voice._scopeHandle = existingScope;
        existingScope._scopeWave = wave;
        startScope(existingScope, graph.analyser);
        // Start extent line animation
        if (existingScope._startExtentAnimation) {
          existingScope._startExtentAnimation();
        }

        // Update info text with detailed params
        if (existingScope._infoText) {
          existingScope._infoText.innerHTML = formatScopeInfo(params, wave, { amp });
          setInfoOpacity(existingScope._infoText, "1");
        }
      } else {
        // No existing scope -- find target or create a live rect
        const scoreSvg = document.querySelector("#scoreInner svg")
                      || document.querySelector(".oscilla-score-inner svg")
                      || document.querySelector("svg");
        if (scoreSvg) {
          // Resolve waveform target:
          //   0. Poly mode: create/reuse live rect with stable group id
          //   1. SVG element by id (score-embedded rect)
          //   2. Another synth's live rect by "scope-{name}" id
          //   3. Fallback: create a new ephemeral live rect
          let targetEl = null;
          let liveRect = null;
          let scopeColor = null;

          if (opts._polyLiveRectUid) {
            // Poly: createLiveAudioRect dedupes, so all voices in the
            // group share one rect regardless of creation order.
            liveRect = createLiveAudioRect(scoreSvg, opts._polyLiveRectUid);
            targetEl = liveRect;
            scopeColor = nextLiveColor();
          }

          if (!targetEl && waveformParam !== "self") {
            targetEl = scoreSvg.getElementById(waveformParam)
                    || scoreSvg.getElementById("scope-" + waveformParam);
          }

          if (targetEl && !scopeColor) {
            // Stacking into an existing element -- get a new distinct color
            scopeColor = nextLiveColor();
          }

          if (!targetEl) {
            // "self" or target not found: create ephemeral rect
            liveRect = createLiveAudioRect(scoreSvg, scopeUid);
            targetEl = liveRect;
            // Use synth type color (chord/sequence/single) for standalone scopes
            scopeColor = getSynthTypeColor(params);
          }

          // A synth run from the console has no score around it to explain:
          // its box is the scope alone, and the line of parameters across
          // the top only when asked for (overlay:1 or 2).
          const showInfo = Number(params.overlay ?? 0) > 0;
          const scopeInfo = showInfo ? formatScopeInfo(params, wave, {
            freqHz: (!source.isChord && source.kind === "osc")
              ? source.node?.frequency?.value : null,
            amp: amp,
            playbackClients: getPlaybackClientsFromEl(cueElement) || getPlaybackClientsFromEl(targetEl)
          }) : null;

          // Render scope into the resolved target element directly.
          // Pass as Element so resolveTarget uses it as-is.
          const scopeHandle = renderScope(scoreSvg, targetEl, scopeUid, {
            element: targetEl,
            info: scopeInfo,
            wave: wave,
            color: scopeColor,
            backgroundColor: targetEl?.dataset?.playbackBackground || null
          });

          if (scopeHandle) {
            voice._scopeHandle = scopeHandle;
            scopeHandle._scopeWave = wave;
            startScope(scopeHandle, graph.analyser);
            // Start extent line animation
            if (scopeHandle._startExtentAnimation) {
              scopeHandle._startExtentAnimation();
            }

            if (scopeHandle._infoText) {
              setInfoOpacity(scopeHandle._infoText, "1");
            }
          }
        }
      }
    }
  }

  // Prepare generators
  installGenerators(voice);

  // Pattern stepping if dynamic
  if (voice._hasStepEngine) {
    scheduleNextStep(voice, true);
  } else if (params.retrigger) {
    // If no pattern engine, but user wants a one-shot envelope retrigger
    applyADSR(graph.gain.gain, ctx, nowSec(ctx), voice.amp, voice.env);
  }
  
  // Duration-based auto stop:
  // - If voice has step engine, dur is treated as step duration.
  // - If no step engine, dur is treated as lifetime stop-after.
  const stopAfter = params.stopAfter ?? params.stop ?? params.lifeDur ?? null;
  const lifetimeDur = stopAfter != null ? stopAfter : (!voice._hasStepEngine ? params.dur : null);
  if (lifetimeDur != null) {
    const beats = resolveVal(lifetimeDur, NaN);
    if (Number.isFinite(beats) && beats > 0) armLifetime(voice, uid, beatsToSec(gridNow().ms, beats) * 1000);
  }

  if (isOscEnabled(ast, params)) {
    sendOSCSynth(ast, params, {
      state: "start",
      wave: String(wave),
      freq: !isChord && source.kind === "osc" ? initialFreq : null,
      amp: voice.amp,
      lifetime
    });
  }

  // Refresh scope info text now that generators are installed
  if (voice._scopeHandle?._infoText) {
    try {
      voice._scopeHandle._infoText.innerHTML = formatScopeInfo(
        voice.params, voice.params.wave ?? "sine",
        { freqHz: voice.source?.node?.frequency?.value, amp: voice.amp }
      );
    } catch { /* ignore */ }
  }

  return voice;
}

/** Hold a param where it is at t, the events after it gone: a ramp starts from there. */
function holdAt(p, t) {
  p.cancelScheduledValues(t); p.setValueAtTime(p.value, t);
}

function updateSynthVoice(uid, ast, cueElement, opts) {
  const ctx = sharedAudioCtx;
  const voice = activeSynthVoices.get(uid);
  if (!voice) return null;

  const params = extractParams(ast);
  voice.ast = ast;
  voice.params = params;
  voice._oneShot = isOnce(params.once);
  // a new envelope takes effect on the next step (envPerStep reads voice.env)
  // or the release; it re-strikes a held note only with retrigger:
  if (params.env != null) voice.env = normaliseEnv(params.env);
  voice.wholeEnv = params.wholeEnv ? normaliseEnv(params.wholeEnv) : null;

  // update region anchor if provided
  if (cueElement) voice._regionEl = cueElement;

  // update wave type (oscillators only -- noise is a different node type)
  if (params.wave != null && voice.source.kind === "osc") {
    const newWave = String(params.wave).toLowerCase();
    const validTypes = ["sine", "square", "sawtooth", "saw", "triangle"];
    if (validTypes.includes(newWave)) {
      const mapped = newWave === "saw" ? "sawtooth" : newWave;
      try {
        if (voice.source.node.type !== mapped) voice.source.node.type = mapped;   // the same again: left be
        voice.source.wave = newWave;
      } catch (e) {
        console.warn(`[synth] Failed to update wave type:`, e);
      }
    }
  }

  // update wave type for chord oscillators
  if (params.wave != null && voice.source.isChord && voice.source.oscillators) {
    const newWave = String(params.wave).toLowerCase();
    const mapped = newWave === "saw" ? "sawtooth" : newWave;
    for (const osc of voice.source.oscillators) {
      try { if (osc.type !== mapped) osc.type = mapped; } catch { /* "custom" type is rejected by the browser */ }
    }
    voice.source.wave = newWave;
  }

  // update lifetime if explicitly given
  if (params.lifetime != null || params.life != null) {
    voice.lifetime = String(params.lifetime ?? params.life).toLowerCase();
  }

  // update amp (amp:sinOsc(...): the envelope's peak is its top, the tremolo the rest).
  // A voice whose steps set its level (envPerStep:, amp:Pwhite(...)) takes
  // the new one at its next step -- a ramp now would cut into the note
  // sounding (a click). Otherwise it glides there from where it is.
  const stepsSetGain = !!(params.envPerStep ?? params.articulate) || params.amp?.type === "pattern";
  // (The same amp again leaves the envelope be -- an attack still rising
  // goes on rising; a new one is the envelope's sustain at that amp.)
  const ampWas = voice.amp;
  const glideAmp = () => {
    if (stepsSetGain || voice.amp === ampWas) return;
    const g = voice.graph.gain.gain, t = nowSec(ctx);
    try {
      holdAt(g, t);
      g.linearRampToValueAtTime(voice.amp * (voice.env?.s ?? 1), t + 0.05);
    } catch { /* ignore */ }
  };
  if (isModulator(params.amp)) {
    const spec = modSpec(params.amp);
    const top = Math.max(spec.lo, spec.hi, 1e-6);
    syncMod(voice, "amp", voice.graph.trem?.gain, params.amp, { spec, scale: 1 / top });
    voice.amp = clamp(top, 0, 0.25);
    glideAmp();
  } else if (params.amp != null) {
    if (syncMod(voice, "amp", null, params.amp) === false && voice.graph.trem) {
      try { voice.graph.trem.gain.setValueAtTime(1, nowSec(ctx)); } catch { /* ignore */ }
    }
    voice.amp = clamp(resolveVal(params.amp, voice.amp), 0, 0.25);
    glideAmp();
  }

  // update pan
  if (voice.graph.panner && params.pan != null && params.pan?.type !== "pattern"   // a pattern steps (runStep)
    && !syncMod(voice, "pan", voice.graph.panner.pan, params.pan)) {                // a modulator moves it
    try {
      voice.graph.panner.pan.linearRampToValueAtTime(clamp(resolveVal(params.pan, 0), -1, 1), nowSec(ctx) + 0.05);
    } catch {
      /* ignore */
    }
  }

  // azi:, elev:, dist: on a voice placed by them: bound afresh
  if (voice.graph.spat && hasSpatial(params)) {
    for (const u of voice._spatUnbinds || []) { try { u(); } catch { /* ignore */ } }
    voice._spatUnbinds = bindSpatial(voice.graph.spat, params);
    voice._unbinders?.push(...voice._spatUnbinds);
  }

  // update filter params
  if (voice.graph.filter && params.filter) {
    const cfRaw = params.filter.freq ?? params.filter.cutoff;
    if (cfRaw != null && !syncMod(voice, "cutoff", voice.graph.filter.frequency, cfRaw)) {
      const cf = resolveVal(cfRaw, null);
      if (cf != null) {
        try {
          voice.graph.filter.frequency.linearRampToValueAtTime(clamp(cf, 20, 20000), nowSec(ctx) + 0.05);
        } catch { /* ignore */ }
      }
    }
    if (params.filter.q != null && !syncMod(voice, "q", voice.graph.filter.Q, params.filter.q)) {
      try {
        voice.graph.filter.Q.linearRampToValueAtTime(clamp(resolveVal(params.filter.q, 0.707), 0.0001, 30), nowSec(ctx) + 0.05);
      } catch { /* ignore */ }
    }
  }

  // update delay mix/time/fb
  if (voice.graph.delay && params.delay) {
    const mix = params.delay.mix ?? params.delay.amount;
    if (mix != null) {
      const m = clamp(mix, 0, 1);
      try {
        voice.graph.delay.dry.gain.linearRampToValueAtTime(1 - m, nowSec(ctx) + 0.05);
        voice.graph.delay.wet.gain.linearRampToValueAtTime(m, nowSec(ctx) + 0.05);
      } catch { /* ignore */ }
    }
    if (params.delay.time != null) {
      try {
        voice.graph.delay.delay.delayTime.linearRampToValueAtTime(clamp(params.delay.time, 0, 5), nowSec(ctx) + 0.05);
      } catch { /* ignore */ }
    }
    const fb = params.delay.fb ?? params.delay.feedback;
    if (fb != null) {
      try {
        voice.graph.delay.fbGain.gain.linearRampToValueAtTime(clamp(fb, 0, FB_MAX), nowSec(ctx) + 0.05);
      } catch { /* ignore */ }
    }
    // the loop's tone: and sat: (feedback.js)
    if (params.delay.tone != null || params.delay.sat != null) voice.graph.delay.loop?.set({ tone: params.delay.tone, sat: params.delay.sat }, nowSec(ctx));
  }

  // update drive: a new curve at once (drive:0 the sound as it is)
  if (voice.graph.shaper && params.drive != null) setDrive(voice.graph.shaper, params.drive);
  // update fold: clip: ring: comb: crush: (a voice made without them stays clean)
  if (voice.graph.grit && ["fold", "clip", "ring", "comb", "crush"].some(k => params[k] != null)) voice.graph.grit.set(params);
  // update fuzz: new settings at once (a voice made without it stays clean)
  if (voice.graph.fuzz && params.fuzz != null) voice.graph.fuzz.set(params.fuzz);
  if (voice.graph.sub && params.sub != null) voice.graph.sub.set(params.sub);

  // update reverb: the mix glides, a new time / damp / pre crossfades
  if (voice.graph.reverb && params.reverb) updateReverb(voice.graph.reverb, params.reverb);

  // update frequency one-shot if constant
  const freqModded = voice.source.kind === "osc" && !voice.source.isChord
    && syncMod(voice, "freq", voice.source.node.frequency, params.freq);
  if (voice.source.kind === "osc" && params.freq != null && !isDynamicParam(params.freq) && !freqModded) {
    const hz = pitchToHz(params.freq);
    const glide = clamp(params.glide ?? voice.glide ?? 0.02, 0, 10);
    voice.glide = glide;
    const f = voice.source.node.frequency, t = nowSec(ctx);
    if (voice.interp === "step" || glide === 0) {
      try { f.cancelScheduledValues(t); f.setValueAtTime(clamp(hz, 1, 20000), t); } catch (err) { console.warn("[synth] freq update failed:", err); }
    } else {
      // from where it is now: a ramp from an old event would leap
      try { holdAt(f, t); f.linearRampToValueAtTime(clamp(hz, 1, 20000), t + Math.min(glide, 0.2)); } catch (err) { console.warn("[synth] freq update failed:", err); }
    }
  }

  // update chord frequencies: ramp each oscillator, add/remove voices as needed
  if (voice.source.isChord && Array.isArray(params.freq)) {
    const newFreqs = params.freq.map(f => pitchToHz(f));
    const oscs = voice.source.oscillators;
    const glide = clamp(params.glide ?? voice.glide ?? 0.02, 0, 10);
    voice.glide = glide;
    const t = nowSec(ctx);
    const rampTime = (voice.interp === "step" || glide === 0) ? 0 : Math.min(glide, 0.2);

    // Update existing oscillators that have a corresponding new freq
    const minLen = Math.min(oscs.length, newFreqs.length);
    for (let i = 0; i < minLen; i++) {
      const hz = clamp(newFreqs[i], 1, 20000);
      try {
        holdAt(oscs[i].frequency, t);
        if (rampTime === 0) {
          oscs[i].frequency.setValueAtTime(hz, t);
        } else {
          oscs[i].frequency.linearRampToValueAtTime(hz, t + rampTime);
        }
      } catch { /* ignore */ }
    }

    // Shrink: disconnect and stop excess oscillators
    if (newFreqs.length < oscs.length) {
      for (let i = newFreqs.length; i < oscs.length; i++) {
        try { oscs[i].stop(t + rampTime + 0.01); } catch { /* already stopped */ }
        try { oscs[i].disconnect(); } catch { /* already disconnected */ }
      }
      oscs.length = newFreqs.length;
    }

    // Grow: add new oscillators for extra frequencies
    if (newFreqs.length > oscs.length) {
      const w = voice.source.wave === "saw" ? "sawtooth" : (voice.source.wave || "sine");
      for (let i = oscs.length; i < newFreqs.length; i++) {
        const osc = ctx.createOscillator();
        osc.type = w;
        osc.frequency.value = clamp(newFreqs[i], 1, 20000);
        osc.connect(voice.source.node); // connect to the chord mixer
        trackOscillator(osc);  // Track for panic cleanup
        attachFM(ctx, osc, fmSpec(voice.params?.fm), trackOscillator);
        osc.start(t);
        oscs.push(osc);
      }
    }

    // Update mixer gain for new voice count
    try {
      voice.source.node.gain.setValueAtTime(
        1.0 / Math.sqrt(oscs.length), t
      );
    } catch { /* ignore */ }

    voice.source.frequencies = newFreqs;
  }

  // retrigger envelope if requested
  if (params.retrigger) {
    voice.env = normaliseEnv(params.env ?? voice.env);
    applyADSR(voice.graph.gain.gain, ctx, nowSec(ctx), voice.amp, voice.env);
  }

  // refresh pattern generators & engine
  installGenerators(voice);

  // (stopped by seq(s, 0): it keeps the new line for seq(s, 1))
  if (voice._hasStepEngine && !voice._timer && !voice._parked) {
    scheduleNextStep(voice, true);
  }
  if (!voice._hasStepEngine && voice._timer) {
    clearTimeout(voice._timer);
    voice._timer = null;
  }

  // update stopAfter
  if (voice._stopTimeout) {
    clearTimeout(voice._stopTimeout);
    voice._stopTimeout = null;
  }
  voice._lifeLeft = null;
  const stopAfter = params.stopAfter ?? params.stop ?? params.lifeDur ?? null;
  const lifetimeDur = stopAfter != null ? stopAfter : (!voice._hasStepEngine ? params.dur : null);
  if (lifetimeDur != null) {
    const beats = resolveVal(lifetimeDur, NaN);
    if (Number.isFinite(beats) && beats > 0) armLifetime(voice, uid, beatsToSec(gridNow().ms, beats) * 1000);
  }

  if (isOscEnabled(ast, params)) {
    sendOSCSynth(ast, params, { state: "update", amp: voice.amp, lifetime: voice.lifetime });
  }

  // Update scope static wave type so stopScope restores correct shape
  if (params.wave != null && voice._scopeHandle) {
    voice._scopeHandle._scopeWave = String(params.wave).toLowerCase();
  }

  // Refresh scope info text with updated params
  if (voice._scopeHandle?._infoText) {
    try {
      const freqHz = voice.source.isChord ? null
        : (voice.source.kind === "osc" ? voice.source.node?.frequency?.value : null);
      voice._scopeHandle._infoText.innerHTML = formatScopeInfo(
        params, params.wave ?? voice.source.wave ?? "sine",
        { freqHz, amp: voice.amp }
      );
    } catch { /* ignore */ }
  }

  return voice;
}

// ============================================================
// 🧩 Pattern stepping
// ============================================================
function isDynamicParam(raw) {
  if (raw == null) return false;
  if (typeof raw === "object" && raw.type === "pattern") return true;
  if (Array.isArray(raw)) return true;
  return false;
}

function installGenerators(voice) {
  const p = voice.params || {};

  function maybeRebuild(key, raw, held = false) {
    const prev = voice[`_${key}Raw`];
    // Only return existing generator if the AST object is exactly the same reference
    if (prev === raw) return voice[`_${key}Gen`];
    
    voice[`_${key}Raw`] = raw;
    return held ? { next: () => raw, lastIndex: 0, _dynamic: false } : buildParamGen(raw);
  }

  // freq:[a, b, c] is a CHORD -- createSource makes one oscillator each and
  // holds them -- not a sequence; sequences are Pseq(...). Read as the array
  // shorthand for Pseq it turned on the step engine, which makes dur: a step
  // length instead of how long the voice lasts: synth(freq:[220, 330],
  // dur:8) then never stopped. A list of lists still steps, chord by chord.
  const heldChord = Array.isArray(p.freq) && !p.freq.some(Array.isArray);
  voice._freqGen = maybeRebuild("freq", p.freq, heldChord);
  voice._ampGen    = maybeRebuild("amp", p.amp);
  voice._cutoffGen = maybeRebuild("cutoff", p.filter?.freq ?? p.filter?.cutoff);
  voice._qGen      = maybeRebuild("q", p.filter?.q);
  voice._durGen    = maybeRebuild("dur", p.dur);
  voice._waveGen   = maybeRebuild("wave", p.wavePattern);
  voice._panGen    = maybeRebuild("pan", p.pan);
  for (const k of SPATIAL_KEYS) voice[`_${k}Gen`] = maybeRebuild(k, p[k]);
  voice._octaveGen = maybeRebuild("octave", p.octave);
  const transposeWas = voice._transposeGen;
  voice._transposeGen = maybeRebuild("transpose", p.transpose);
  if (voice._transposeGen !== transposeWas) voice._transposeBar = null;   // a new form: from its first bar

  // Build synthConfig generator for pattern parameter containing synth configs
  // e.g. synth(pattern:Prand([synth(wave:sine, freq:~A4), synth(wave:square, freq:~A2)], 5))
  voice._synthConfigGen = maybeRebuild("synthConfig", p.pattern);
  voice._genStack = null;  // Reset generator stack for nested patterns
  voice._every = makeEvery(p.every);   // counted again from each run of the line

  voice._hasStepEngine =
    Boolean(voice._freqGen?._dynamic) ||
    Boolean(voice._ampGen?._dynamic) ||
    Boolean(voice._cutoffGen?._dynamic) ||
    Boolean(voice._qGen?._dynamic) ||
    Boolean(voice._durGen?._dynamic) ||
    Boolean(voice._waveGen?._dynamic) ||
    Boolean(voice._panGen?._dynamic) ||
    SPATIAL_KEYS.some(k => voice[`_${k}Gen`]?._dynamic) ||
    Boolean(voice._octaveGen?._dynamic) ||
    Boolean(voice._transposeGen?._dynamic) ||
    Boolean(voice._synthConfigGen?._dynamic) ||
    Boolean(p.envPerStep) ||
    Boolean(p.articulate) ||
    // chance: / every: with a dur: -- a step to draw for, nothing else changing
    // (once: is drawn for as a whole, by the dispatcher: cueUtils stepsItself)
    (p.dur != null && !isOnce(p.once) && (p.chance != null || voice._every != null));
}


// a release longer than this (seconds) lets a stepping sound play on under its fade
const FADE_STEPPING = 0.3;
// those doing so: out of activeSynthVoices, so a panic finds them here
const fadingVoices = new Set();

/**
 * Does `voice` take its next step? Playing (not replaced by a voice of the
 * same uid); or stopped and fading out (stopSingleVoice), until the fade's
 * end -- unless its line has been run again meanwhile, the new voice taking over.
 */
function stepsOn(voice) {
  if (!voice._stopped) return activeSynthVoices.get(voice.uid) === voice;
  return voice._fadeEnd > sharedAudioCtx.currentTime && !activeSynthVoices.has(voice.uid);
}

function scheduleNextStep(voice, first = false) {
  // Check if voice was stopped or replaced by a new voice with same uid
  if (!stepsOn(voice)) {
    voice._stopped = true;
    return;
  }

  // once:1 -- the one step has sounded: let the voice go, its tail ringing
  if (!first && voice._oneShot) {
    stopSynth(voice.uid, voice.env?.r ?? 0.05);
    return;
  }

  // seq(s, 0): the note before this one was the last -- it stops here
  if (!first && voice._seqStop) {
    parkVoice(voice, voice._nextSec ?? sharedAudioCtx.currentTime);
    return;
  }

  // ========== SynthConfig Pattern Support ==========
  // If we have a pattern of synth configs, get the next one and use its args
  // e.g. synth(pattern:Prand([synth(wave:sine, freq:~A4, dur:2), synth(wave:square, freq:~A2, dur:3)], 5))
  // Supports nested patterns: synth(pattern:Prand(synth(pattern:Pseq(...)), synth(...), ...))
  // Supports patterns in properties: synth(pattern:Prand(synth(freq:Pseq(~A4, ~B4, ~C5, 4), dur:0.25), ...))
  let stepConfig = null;
  if (voice._hold && voice._currentStepConfig) stepConfig = voice._currentStepConfig;   // hold(): the same again
  else if (voice._synthConfigGen && voice._synthConfigGen._dynamic) {
    // Initialize generator stack if needed
    if (!voice._genStack) {
      voice._genStack = [voice._synthConfigGen];
    }

    // Check if args has any pattern properties (freq, dur, amp as patterns)
    const hasPatternProperties = (args) => {
      if (!args) return false;
      for (const key of ["freq", "dur", "amp", "cutoff", "q"]) {
        const val = args[key];
        if (val && typeof val === "object" && val.type === "pattern") {
          return true;
        }
      }
      return false;
    };

    // Create a sub-generator from a synthConfig with pattern properties
    // e.g. synth(freq:Pseq(a, b, c, 2), dur:0.25) -> generator yielding {freq:a, dur:0.25}, {freq:b, dur:0.25}, etc.
    const createPropertyPatternGen = (args) => {
      // Build generators for each pattern property
      const propGens = {};
      let mainPatternKey = null;

      for (const key of ["freq", "dur", "amp", "cutoff", "q"]) {
        const val = args[key];
        if (val && typeof val === "object" && val.type === "pattern") {
          propGens[key] = buildParamGen(val);
          if (!mainPatternKey) {
            mainPatternKey = key;
          }
        }
      }

      if (!mainPatternKey) return null;

      return {
        next: () => {
          // Check if we've exceeded repeats (for non-infinite patterns)
          // Note: the pattern generator itself handles repeats, so we just check for null
          const result = { ...args };
          let anyNull = false;

          for (const [key, gen] of Object.entries(propGens)) {
            const v = gen.next();
            if (v == null) {
              anyNull = true;
              break;
            }
            result[key] = v;
          }

          if (anyNull) return null;
          // Remove the pattern objects from result, keep resolved values
          return result;
        },
        _dynamic: true
      };
    };

    // Helper to get next leaf synthConfig (recursively expands nested patterns)
    const getNextLeafConfig = () => {
      while (voice._genStack.length > 0) {
        const topGen = voice._genStack[voice._genStack.length - 1];
        const cfg = topGen.next();

        if (cfg == null) {
          // Current generator exhausted, pop and try parent
          voice._genStack.pop();
          continue;
        }

        // Check if this is a synthConfig with a nested pattern
        if (cfg && typeof cfg === "object" && cfg.type === "synthConfig" && cfg.args) {
          // First check for explicit pattern: parameter
          if (cfg.args.pattern && typeof cfg.args.pattern === "object" && cfg.args.pattern.type === "pattern") {
            // Has nested pattern - create sub-generator and push onto stack
            const subGen = buildParamGen(cfg.args.pattern);
            if (subGen && subGen._dynamic) {
              voice._genStack.push(subGen);
              continue;  // Pull from the new sub-generator
            }
          }

          // Check for pattern properties (freq:Pseq(...), etc.)
          if (hasPatternProperties(cfg.args)) {
            const propGen = createPropertyPatternGen(cfg.args);
            if (propGen) {
              voice._genStack.push(propGen);
              continue;  // Pull from the property pattern generator
            }
          }

          // No nested patterns - return this config's args
          return cfg.args;
        }

        // Check if cfg itself is a plain object with resolved values (from property pattern gen)
        if (cfg && typeof cfg === "object" && !cfg.type) {
          return cfg;
        }

        // Not a synthConfig object, return null
        return null;
      }
      // All generators exhausted
      return null;
    };

    stepConfig = getNextLeafConfig();
    if (stepConfig == null && voice._genStack.length === 0) {
      // All patterns exhausted — rebuild the generator and retry
      voice._synthConfigGen = buildParamGen(voice.params?.pattern);
      voice._genStack = voice._synthConfigGen?._dynamic ? [voice._synthConfigGen] : null;
      if (voice._genStack) stepConfig = getNextLeafConfig();
      if (stepConfig == null) { stopSynth(voice.uid); return; }
    }
  }
  voice._currentStepConfig = stepConfig;  // Store for applyStepTargets
  // ==================================================

  // Step duration:
  // - if synthConfig has dur, use that
  // - else if dur is dynamic -> per-step
  // - else default to 1.0
  let dur = 1.0;
  let targetDur = null;  // Track the raw pattern value for scope highlighting

  // Priority: stepConfig.dur > _durGen > params.dur
  if (stepConfig?.dur != null) {
    const v = Number(stepConfig.dur);
    if (Number.isFinite(v) && v > 0) {
      dur = v;
      targetDur = v;
    }
  } else if (voice._durGen && voice._durGen._dynamic) {
    // pattern exhausted: rebuilt and carried on (draw); still nothing: stop
    const v = draw(voice, "dur", voice.params?.dur);
    if (v == null) { stopSynth(voice.uid); return; }
    targetDur = resolveVal(v, null);  // rand()/hrand() steps resolve here
    dur = targetDur || dur;
  } else if (voice.params?.dur != null && voice._hasStepEngine) {
    const v = Number(voice.params.dur);
    if (Number.isFinite(v) && v > 0) dur = v;
  }

  // Steps on the audio clock: step n+1 is step n plus its dur, exactly --
  // not "a timer of dur after the last one fired" (timers fire late, and
  // that added up: patterns drifted off each other and off the beat). Each
  // is scheduled ahead (STEP_LEAD_MS) at its exact moment.
  const ctx = sharedAudioCtx;
  let t = first ? (voice._stepStartSec ?? ctx.currentTime) : (voice._nextSec ?? ctx.currentTime);
  if (t < ctx.currentTime - 0.25) t = ctx.currentTime;   // fell far behind (a hidden tab): carry on from now
  t = Math.max(t, ctx.currentTime);
  // dur: is in beats -- seconds at the tempo from here (a change of tempo
  // inside the step is counted where it falls)
  dur = Math.max(0.02, beatsAt(t, dur));
  applyStepTargets(voice, dur, targetDur, t);
  voice._nextSec = t + dur;

  if (!stepsOn(voice)) return;

  voice._timer = setTimeout(() => {
    voice._timer = null;
    // Check if voice was stopped or replaced
    if (!stepsOn(voice)) return;
    scheduleNextStep(voice, false);
  }, Math.max(0, (voice._nextSec - sharedAudioCtx.currentTime) * 1000 - STEP_LEAD_MS));
}

/**
 * The audio clock's time that is HEARD at a performance.now() instant
 * (getOutputTimestamp pairs the two; currentTime runs ahead of it by the
 * output latency). Used once, for the grid's anchor.
 */
function heardAudioTime(ms) {
  const ctx = sharedAudioCtx;
  const ts = ctx.getOutputTimestamp?.();
  return ts && ts.performanceTime > 0
    ? ts.contextTime + (ms - ts.performanceTime) / 1000
    : ctx.currentTime + (ms - performance.now()) / 1000;
}

// name -> { gain, amount, chance, every }: a voice's send (send:), opened or shut each step when chance: or every: is given
const sendsOf = new Map();

/** Does this step go into the send? Its every: count first, then chance:'s dice. */
function sendOpen({ chance, every }) {
  if (every && !every.on()) return false;
  return chance == null || Math.random() < chance;
}

function applyStepTargets(voice, dur, targetDur = null, atSec = null) {
  const ctx = sharedAudioCtx;
  const t = atSec ?? nowSec(ctx);                // the step's moment on the audio clock

  // overlap: this step's note is new -- its own oscillator, envelope and
  // fader, starting from silence (nothing in flight is ever rescheduled:
  // cancelling a sounding note's release changed it at once, a click, 40 ms
  // before the note). N sounding already: the one ending soonest fades out
  // over 8 ms on its fader, which nothing else moves.
  let lane = null;
  if (voice.lanes) {
    const ov = voice.lanes;
    ov.notes = ov.notes.filter(nt => nt.endsAt > t);
    if (ov.notes.length >= ov.n) {
      const old = ov.notes.reduce((a, b) => (b.endsAt < a.endsAt ? b : a));
      try {
        old.fade.gain.setValueAtTime(1, t);
        old.fade.gain.linearRampToValueAtTime(0, t + 0.008);
        old.source.node.stop(t + 0.012);
      } catch { /* ignore */ }
      ov.notes = ov.notes.filter(nt => nt !== old);
    }
    const src = createSource(ctx, ov.wave, 440, fmSpec(voice.params?.fm));
    const env = ctx.createGain(), fade = ctx.createGain();
    env.gain.value = 0;
    src.node.connect(env).connect(fade).connect(ov.into);
    try { src.node.start(t); } catch { /* ignore */ }
    src.node.onended = () => { try { src.node.disconnect(); env.disconnect(); fade.disconnect(); } catch { /* ignore */ } };
    lane = { source: src, env, fade, endsAt: Infinity, at: t };
    ov.notes.push(lane);
    voice.source = src;
  }

  // Brief scope info flash on step change
  if (voice._scopeHandle?._infoText) {
    setInfoOpacity(voice._scopeHandle._infoText, "1");
  }

  // Get current step config (if using pattern of synth configs)
  const stepConfig = voice._currentStepConfig;

  // Frequency (supports single notes and chord arrays in patterns)
  // Priority: stepConfig.freq > _freqGen > static params
  let targetFreqHz = null;  // Track target frequency for scope highlighting
  let isRestStep = false;   // Track if this step is a rest

  // Determine frequency value: from stepConfig or from _freqGen
  let freqValue = null;
  if (stepConfig?.freq != null) {
    freqValue = stepConfig.freq;
  } else if (voice._freqGen && voice._freqGen._dynamic) {
    freqValue = draw(voice, "freq", voice.params?.freq);   // exhausted: rebuilt and carried on
  }

  // octave:Prand(0, 1, -1, inf) -- an octave drawn each step, under a note
  // that is not itself a pattern too
  if (freqValue == null && (voice._octaveGen?._dynamic || voice._transposeGen?._dynamic)) freqValue = voice.params?.freq ?? 440;

  // chance:1/6 -- this step sounds with that chance, else it is a rest (its
  // patterns have moved on all the same); after one, a voice with no pitch of
  // its own to draw is given its note again
  // every:4 -- only each 4th step sounds (every:peuclid(3, 8) those it gives a
  // 1), and chance: draws only for those
  const chance = voice.params?.chance != null && Number.isFinite(Number(voice.params.chance)) ? Number(voice.params.chance) : null;
  const shut = voice._every ? !voice._every.on() : false;
  if (shut || (chance != null && !(Math.random() < chance))) freqValue = "r";
  else if ((chance != null || voice._every) && freqValue == null && voice._isResting) freqValue = voice.params?.freq ?? 440;

  if (freqValue != null) {
    // stack:[0, 7]: each note with these above it -- a chord of it
    let v = voice.params?.stack != null && !isRest(freqValue) ? stackNotes(freqValue, voice.params.stack) : freqValue;
    // octave: up or down so many octaves (drawn each step from a pattern)
    if (voice._octaveGen && !isRest(v)) v = shiftOctaves(v, voice._octaveGen._dynamic ? draw(voice, "octave") : voice.params.octave);
    // transpose: so many semitones, a new one each bar (transposeAt)
    if (voice.params?.transpose != null && !isRest(v)) v = shiftSemitones(v, transposeAt(voice, t));
    // Check for rest value (r, ~r, rest, ~rest)
    if (isRest(v)) {
        isRestStep = true;
        targetFreqHz = "rest";
        // Silence by setting frequency to 0 - avoids gain/envelope conflicts
        const osc = voice.source?.node;
        if (osc?.frequency) {
          osc.frequency.cancelScheduledValues(t);
          osc.frequency.setTargetAtTime(0, t, 0.02); // smooth glide to 0Hz
        }
        voice._isResting = true;
      } else {
        // Coming out of a rest - just clear the flag, let envelope handle attack
        if (voice._isResting) {
          voice._isResting = false;
        }

        const glide = clamp(voice.params?.glide ?? voice.glide ?? 0.02, 0, 10);
        voice.glide = glide;
        const RAMP = Math.min(0.005, dur * 0.25); // 2–5 ms

        // Check if this step is a chord (array) or single note
        if (Array.isArray(v) && v.length > 0) {
        // Chord: update or create oscillators for each note
        const newFreqs = v.map(f => pitchToHz(f));
        targetFreqHz = newFreqs;  // Pass all frequencies for chord matching

        // Ensure we have chord oscillators
        if (!voice.source.isChord || !voice.source.oscillators) {
          // Convert from single osc to chord - create new oscillators
          const ctx = sharedAudioCtx;
          const oldNode = voice.source.node;
          const wave = voice.params?.wave || "sine";

          voice.source.oscillators = newFreqs.map(hz => {
            const osc = ctx.createOscillator();
            osc.type = OSC_WAVES[String(wave).toLowerCase()] ?? wave;
            osc.frequency.value = clamp(hz, 1, 20000);
            trackOscillator(osc);
            attachFM(ctx, osc, fmSpec(voice.params?.fm), trackOscillator);
            return osc;
          });

          // Create mixer for chord
          const mixer = ctx.createGain();
          mixer.gain.value = 1.0 / Math.sqrt(voice.source.oscillators.length);
          voice.source.oscillators.forEach(osc => {
            osc.connect(mixer);
            osc.start(t);
          });

          // Reconnect to graph
          if (oldNode) {
            try { oldNode.disconnect(); } catch { /* already disconnected */ }
          }
          // where the single note went in (it skipped the sub, drive, fuzz -- and,
          // into the filter, the envelope -- going straight to filter or gain)
          mixer.connect(voice.graph.sourceInto || voice.graph.gain);

          voice.source.node = mixer;
          voice.source.isChord = true;
          voice.source.frequencies = newFreqs;
        } else {
          // Already a chord - update frequencies
          const oscs = voice.source.oscillators;
          const ctx = sharedAudioCtx;
          const wave = voice.params?.wave || "sine";

          // Update existing oscillators, add/remove as needed
          for (let i = 0; i < Math.max(oscs.length, newFreqs.length); i++) {
            if (i < newFreqs.length && i < oscs.length) {
              // Update existing oscillator
              const p = oscs[i].frequency;
              try {
                if (voice.interp === "step" || glide === 0) {
                  p.cancelScheduledValues(t);
                  p.setValueAtTime(p.value, t);
                  p.linearRampToValueAtTime(clamp(newFreqs[i], 1, 20000), t + RAMP);
                } else {
                  // Portamento: ramp from current value
                  p.cancelScheduledValues(t);
                  p.setValueAtTime(p.value, t);
                  p.linearRampToValueAtTime(clamp(newFreqs[i], 1, 20000), t + Math.min(glide, dur));
                }
              } catch (err) { console.warn("[synth] chord freq step failed:", err); }
            } else if (i >= oscs.length && i < newFreqs.length) {
              // Need new oscillator
              const osc = ctx.createOscillator();
              osc.type = OSC_WAVES[String(wave).toLowerCase()] ?? wave;
              osc.frequency.value = clamp(newFreqs[i], 1, 20000);
              trackOscillator(osc);
              attachFM(ctx, osc, fmSpec(voice.params?.fm), trackOscillator);
              osc.connect(voice.source.node);  // Connect to mixer
              osc.start(t);
              oscs.push(osc);
            } else if (i < oscs.length && i >= newFreqs.length) {
              // Remove excess oscillator
              try {
                oscs[i].stop(t + 0.01);
                oscs[i].disconnect();
              } catch { /* already stopped */ }
            }
          }
          // Trim array
          voice.source.oscillators = oscs.slice(0, newFreqs.length);
          voice.source.frequencies = newFreqs;
          // Update mixer gain
          voice.source.node.gain.value = 1.0 / Math.sqrt(newFreqs.length);
        }
      } else if (voice.source.kind === "osc" || voice.source.node?.frequency) {
        // Single note
        const hz = pitchToHz(v);
        targetFreqHz = hz;

        // If currently a chord, switch back to single osc
        if (voice.source.isChord && voice.source.oscillators) {
          // Stop all chord oscillators except first
          for (let i = 1; i < voice.source.oscillators.length; i++) {
            try {
              voice.source.oscillators[i].stop(t + 0.01);
              voice.source.oscillators[i].disconnect();
            } catch { /* already stopped */ }
          }
          voice.source.oscillators = [voice.source.oscillators[0]];
          voice.source.isChord = false;
          voice.source.node.gain.value = 1.0;
        }

        const freqNode = voice.source.oscillators?.[0]?.frequency || voice.source.node?.frequency;
        if (freqNode && lane) {
          // a new note: straight to its pitch -- or, with glide:, from the last
          // note's pitch to its own over glide (the notes before ring on where they are)
          const to = clamp(hz, 1, 20000), from = voice._lastNoteHz;
          try {
            freqNode.cancelScheduledValues(t);
            if (glide > 0 && from > 0 && from !== to) {
              freqNode.setValueAtTime(from, lane.at);
              freqNode.exponentialRampToValueAtTime(to, lane.at + glide);
            } else freqNode.setValueAtTime(to, lane.at);
          } catch { /* ignore */ }
          voice._lastNoteHz = to;
        } else if (freqNode) {
          if (voice.interp === "step" || glide === 0) {
            try {
              freqNode.cancelScheduledValues(t);
              freqNode.setValueAtTime(freqNode.value, t);
              freqNode.linearRampToValueAtTime(clamp(hz, 1, 20000), t + RAMP);
            } catch (err) { console.warn("[synth] freq step failed:", err); }
          } else {
            // Portamento: ramp from current value to new frequency
            try {
              freqNode.cancelScheduledValues(t);
              freqNode.setValueAtTime(freqNode.value, t);
              freqNode.linearRampToValueAtTime(clamp(hz, 1, 20000), t + Math.min(glide, dur));
            } catch (err) { console.warn("[synth] freq step failed:", err); }
          }
        }
      }
    } // end else (not a rest)
  }

  // Amp -- step gain when amp is dynamic or comes from stepConfig
  // Priority: stepConfig.amp > _ampGen > static amp
  // Static amp is controlled by applyADSR; clobbering it here
  // would destroy the attack/release envelope.
  let ampValue = null;
  if (stepConfig?.amp != null) {
    ampValue = stepConfig.amp;
  } else if (voice._ampGen && voice._ampGen._dynamic) {
    ampValue = draw(voice, "amp");
  }

  const envPerStep = voice.params?.envPerStep ?? voice.params?.articulate ?? false;
  if (ampValue != null) {
    voice.amp = clamp(ampValue, 0, 0.25);
    // struck each step: the envelope below peaks at this amp instead
    if (!isRestStep && !envPerStep) {
      try { voice.graph.gain.gain.linearRampToValueAtTime(voice.amp, t + 0.05); } catch (err) { console.warn("[synth] amp step failed:", err); }
    }
  }

  // On the signal bus, under the synth's own name (poly voices bass__0 ...
  // as bass): freq (Hz), amp, and t -- where the pitch is between the
  // lowest and highest it has played, 0..1, for follow(bass.t, lo, hi).
  if (!isRestStep) {
    const hz = Array.isArray(targetFreqHz) ? targetFreqHz[0]
      : typeof targetFreqHz === "number" ? targetFreqHz
      : voice.source?.node?.frequency?.value;
    // when the step is HEARD, not when it is scheduled (STEP_LEAD_MS and the
    // output's latency ahead): a picture on trig:bass.hit lands with the note
    if (Number.isFinite(hz) && hz > 0) {
      const wait = (t - heardAudioTime(performance.now())) * 1000;
      if (wait > 2) setTimeout(() => publishPitch(voice, hz), wait);
      else publishPitch(voice, hz);
    }
  }

  // wave:Pchoose(square, noise, ...): this step's wave (a rest keeps the last)
  if (voice._waveGen?._dynamic && !isRestStep) {
    const w = draw(voice, "wave", voice.params?.wavePattern);
    switchWave(voice, w, t);
  }

  // Per-step envelope retriggering (envPerStep / articulate mode)
  // When enabled, each sequence step gets its own ADSR cycle
  // Skip for rest steps - they should remain silent
  // A dynamic amp (a pattern: accents) is each step's peak.
  if (lane && isRestStep) {
    // a rest: the note made for this step is not sounded -- gone at once
    try { lane.source.node.stop(t + 0.01); } catch { /* ignore */ }
    voice.lanes.notes = voice.lanes.notes.filter(nt => nt !== lane);
  } else if (envPerStep && !isRestStep && lane) {
    // overlap: the whole envelope -- held to the end of the step, then its
    // full release, ringing on under the next notes in the other lanes
    // N notes can sound at once: each at 1/sqrt(N), as a chord's notes are --
    // full each, they piled up into the limiter at every attack
    const env = voice.env, peak = voice.amp / Math.sqrt(voice.lanes.n);
    const A = Math.max(0.003, env.a), D = Math.max(0, env.d), R = Math.max(0.005, env.r), S = clamp(env.s, 0, 1);
    const g = lane.env.gain;
    const t1 = lane.at;
    try {
      // a new note: nothing scheduled on it yet
      g.setValueAtTime(0, t1);
      g.linearRampToValueAtTime(peak, t1 + A);
      const held = Math.max(t1 + A, t + dur);
      if (D > 0) g.linearRampToValueAtTime(peak * S, Math.min(t1 + A + D, held));
      g.setValueAtTime(peak * S, held);
      g.linearRampToValueAtTime(0, held + R);
      lane.endsAt = held + R;
      lane.source.node.stop(held + R + 0.05);       // gone when its release is
      voice._releaseAt = held;
    } catch (e) {
      console.error("[synth] overlap envelope error:", e);
    }
  } else if (envPerStep && !isRestStep) {
    const env = voice.env;
    const amp = voice.amp;
    const gainParam = voice.graph.gain.gain;

    // Calculate timing: attack + decay + sustain + release must fit in dur
    const A = Math.max(0.001, env.a);
    const D = Math.max(0, env.d);
    const R = Math.max(0.001, env.r);
    const S = clamp(env.s, 0, 1);

    // Time available for sustain = dur - A - D - R
    // If not enough time, compress proportionally
    const totalEnvTime = A + D + R;
    let scaledA = A, scaledD = D, scaledR = R;
    if (totalEnvTime > dur * 0.95) {
      // Envelope is longer than the step: squeeze the decay and release into
      // it -- the attack keeps at least 5 ms (squeezed with the rest, a long
      // release took it under a millisecond: a click, whatever a: said)
      const room = dur * 0.95;
      scaledA = Math.min(A, Math.max(0.005, room * 0.25));
      const k = D + R > 0 ? Math.max(0, room - scaledA) / (D + R) : 1;
      scaledD = D * Math.min(1, k);
      scaledR = Math.max(Math.min(R, 0.003), R * Math.min(1, k));
    }
    // the release ends inside the step (at 95% of it): ended exactly at the
    // next step's start, that step's cancel took the ramp away -- the note
    // held at sustain and was cut to 0 at once (a click on every note)
    const sustainTime = Math.max(0, dur * 0.95 - scaledA - scaledD - scaledR);

    const peak = amp;
    const sus = amp * S;

    try {
      // the last note's release is over by now (it ends inside its step): from
      // 0. (Not cancelAndHoldAtTime: asked for a time ahead while a release
      // still runs, Chrome holds it at once -- a jump, 40 ms early)
      gainParam.cancelScheduledValues(t);
      gainParam.setValueAtTime(0, t);
      // Attack: 0 -> peak
      gainParam.linearRampToValueAtTime(peak, t + scaledA);
      // Decay: peak -> sustain
      if (scaledD > 0) {
        gainParam.linearRampToValueAtTime(sus, t + scaledA + scaledD);
      } else {
        gainParam.setValueAtTime(sus, t + scaledA);
      }
      // Hold sustain, then release
      const releaseStart = t + scaledA + scaledD + sustainTime;
      voice._releaseAt = releaseStart;      // seq(s, 0) under a whole env: lets the note ring on from here
      gainParam.setValueAtTime(sus, releaseStart);
      gainParam.linearRampToValueAtTime(0, releaseStart + scaledR);
    } catch (e) {
      console.error("[synth] envPerStep error:", e);
    }
  }

  const RAMP = Math.min(0.01, dur * 0.25); // 2–10 ms, step-aware

  // cutoff - Priority: stepConfig.cutoff > _cutoffGen
  let cutoffValue = null;
  if (stepConfig?.cutoff != null) {
    cutoffValue = stepConfig.cutoff;
  } else if (stepConfig?.filter?.cutoff != null) {
    cutoffValue = stepConfig.filter.cutoff;
  } else if (stepConfig?.filter?.freq != null) {
    cutoffValue = stepConfig.filter.freq;
  } else if (voice._cutoffGen && voice._cutoffGen._dynamic) {
    cutoffValue = draw(voice, "cutoff");
  }

  if (cutoffValue != null && voice.graph.filter) {
    const p = voice.graph.filter.frequency;
    try {
      p.cancelScheduledValues(t);
      p.setValueAtTime(p.value, t);
      p.linearRampToValueAtTime(clamp(cutoffValue, 20, 20000), t + RAMP);
    } catch (err) { console.warn("[synth] cutoff step failed:", err); }
  }

  // Q - Priority: stepConfig.q > _qGen
  let qValue = null;
  if (stepConfig?.q != null) {
    qValue = stepConfig.q;
  } else if (stepConfig?.filter?.q != null) {
    qValue = stepConfig.filter.q;
  } else if (voice._qGen && voice._qGen._dynamic) {
    qValue = draw(voice, "q");
  }

  if (qValue != null && voice.graph.filter) {
    const p = voice.graph.filter.Q;
    try {
      p.cancelScheduledValues(t);
      p.setValueAtTime(p.value, t);
      p.linearRampToValueAtTime(clamp(qValue, 0.0001, 30), t + RAMP);
    } catch (err) { console.warn("[synth] q step failed:", err); }
  }

  // pan:Pwhite(-1, 1): each step somewhere else (a rest keeps the last place)
  if (voice._panGen?._dynamic && !isRestStep && voice.graph.panner) {
    const v = draw(voice, "pan", voice.params?.pan);
    const pan = resolveVal(v, null);
    if (pan != null) {
      const p = voice.graph.panner.pan;
      try {
        p.cancelScheduledValues(t);
        p.setValueAtTime(p.value, t);
        p.linearRampToValueAtTime(clamp(pan, -1, 1), t + RAMP);
      } catch (err) { console.warn("[synth] pan step failed:", err); }
    }
  }

  // azi:pseq(0, 90, 180, -90, inf): each step at another speaker
  if (voice.graph.spat && !isRestStep) {
    const at = {};
    for (const k of SPATIAL_KEYS) {
      if (!voice[`_${k}Gen`]?._dynamic) continue;
      const x = resolveVal(draw(voice, k, voice.params?.[k]), null);
      if (Number.isFinite(Number(x))) at[k] = Number(x);
    }
    if (Object.keys(at).length) voice.graph.spat.set(at, RAMP / 3, t);
  }


  // Update scope info text with current live values
  if (voice._scopeHandle?._infoText) {
    try {
      const freqHz = targetFreqHz ?? voice.source?.node?.frequency?.value;
      const freqIndex = voice._freqGen?.lastIndex ?? null;
      const durIndex = voice._durGen?.lastIndex ?? null;
      const durForDisplay = targetDur ?? dur;
      voice._scopeHandle._infoText.innerHTML = formatScopeInfo(
        voice.params, voice.params.wave ?? "sine",
        { freqHz, freqIndex, durIndex, amp: voice.amp, dur: durForDisplay }
      );
    } catch { /* ignore */ }
  }

  // a note struck: FM brightness from full (fm decay:), a kick's pitch drop
  // send:{..., every:, chance:} -- this step into the bus, or not (the sound itself either way)
  const snd = sendsOf.get(String(voice.uid ?? "").split("__")[0]);
  if (snd && (snd.chance != null || snd.every)) {
    const open = sendOpen(snd);       // counted on a rest too: every:6 is every 6th step
    try { snd.gain.gain.setValueAtTime(!isRestStep && open ? snd.amount : 0, t); } catch { /* gone */ }
  }
  if (!isRestStep) strike(voice, t, typeof targetFreqHz === "number" ? targetFreqHz : null);

  // OSC step, with its moment on the grid (at): the server can tag it for
  // that moment (--osc-timetags), so SuperCollider plays it on the beat
  if (isOscEnabled(voice.ast, voice.params)) {
    const hz = voice.source.kind === "osc" ? voice.source.node.frequency.value : null;
    const at = gridMsOfAudio(t);
    sendOSCSynth(voice.ast, voice.params, { state: "step", freq: hz, amp: voice.amp, dur, ...(at != null ? { at } : {}) });
  }
}

/** A note begins at t (synthTimbre.js): FM's index envelope, a kick's fall. */
function strike(voice, t, hz = null) {
  const oscs = voice.source?.oscillators || (voice.source?.kind === "osc" ? [voice.source.node] : []);
  for (const o of oscs) fmStrike(o, t);
  if (voice.params?._drum === "kick" && voice.source?.kind === "osc") {
    kickStrike(voice.source.node.frequency, hz ?? pitchToHz(voice.params.freq ?? 50), t);
  }
}

// ============================================================
// 🧹 Cleanup
// ============================================================
function cleanupVoice(uid, voice) {
  fadingVoices.delete(voice);
  voice._fadeEnd = 0;
  if (voice._timer) { clearTimeout(voice._timer); voice._timer = null; }
  stopMods(voice);
  // ========== CONTROL PLANE: Unbind all subscriptions ==========
  if (voice._unbinders) {
    voice._unbinders.forEach(unbind => {
      try { unbind(); } catch (e) { /* ignore */ }
    });
    voice._unbinders = [];
  }
  // ==============================================================

  // Disconnect chord oscillators if present
  if (voice.source?.isChord && voice.source?.oscillators) {
    voice.source.oscillators.forEach(osc => {
      try { osc.stop(); } catch { /* ignore */ }
      try { osc.disconnect(); } catch { /* ignore */ }
    });
  }

  // overlap: every note still sounding
  for (const nt of voice.lanes?.notes || []) {
    try { nt.source.node.stop(); } catch { /* ignore */ }
    try { nt.source.node.disconnect(); nt.env.disconnect(); nt.fade.disconnect(); } catch { /* ignore */ }
  }

  // Force-stop then disconnect source (backstop for noise BufferSourceNodes
  // whose scheduled stop() may not have fired yet)
  try { voice.source?.node?.stop?.(); } catch { /* ignore */ }
  try { voice.source?.node?.disconnect(); } catch { /* ignore */ }
  for (const n of Object.values(voice.source?.switch?.noises || {})) {
    try { n.src.stop(); } catch { /* ignore */ }
    try { n.src.disconnect(); } catch { /* ignore */ }
  }
  try { voice.graph?.gain?.disconnect(); } catch { /* ignore */ }
  try { voice.graph?.panner?.disconnect(); } catch { /* ignore */ }
  voice.graph?.spat?.dispose();
  try { voice.graph?.exit?.disconnect(); } catch { /* ignore */ }
  try { voice.graph?.filter?.disconnect(); } catch { /* ignore */ }

  try { voice.graph?.delay?.dry?.disconnect(); } catch { /* ignore */ }
  try { voice.graph?.delay?.wet?.disconnect(); } catch { /* ignore */ }
  try { voice.graph?.delay?.delay?.disconnect(); } catch { /* ignore */ }
  try { voice.graph?.delay?.fbGain?.disconnect(); } catch { /* ignore */ }

  disconnectReverb(voice.graph?.reverb);
  try { voice.graph?.pre?.disconnect(); } catch { /* ignore */ }

  try { voice.graph?.sum?.disconnect(); } catch { /* ignore */ }
  try { voice.graph?.analyser?.disconnect(); } catch { /* ignore */ }

  // IDENTITY GUARD: this cleanup runs on a delay (release + FX tail, up
  // to ~15s after stop). If the cue was retriggered in the meantime, a NEW
  // voice owns this uid — deleting it here would evict a live voice from
  // the registry, making it unstoppable (a "zombie" that no stopSynth can
  // find). Only touch registry/group/scope state if we still own the uid.
  const stillOwner = activeSynthVoices.get(uid) === voice;

  // Scope cleanup: for poly sub-voices, destroy the scope group
  // entirely to avoid stale static waves piling up. For mono voices,
  // keep the static wave as visual feedback.
  // stopSingleVoice has already taken the voice out of the registry, so
  // "still the owner" would never hold for a stopped one: what matters is
  // that no NEW voice has taken this uid since.
  // A score element keeps its static wave as a picture of what it plays; a
  // poly voice's scope, and the trace in a console's box, go -- the box
  // stays (where it was put, as viz() styled it) for the next run.
  const isPoly = uid.includes("__");
  const inLiveBox = voice._scopeHandle?._targetEl?.dataset?.liveAudio != null;
  const taken = activeSynthVoices.has(uid) && !stillOwner;
  if (!taken && (isPoly || inLiveBox) && voice._scopeHandle) {
    const scopeUid = `scope-${uid}`;
    destroyWaveform(scopeUid);
  }
  voice._scopeHandle = null;

  if (stillOwner) {
    activeSynthVoices.delete(uid);
    waitTag(uid);
    unregisterAnalyser(uid, voice.graph?.analyser);

    // Remove from poly group if this is a sub-voice (uid contains __)
    const sepIdx = uid.lastIndexOf("__");
    if (sepIdx > 0) {
      const baseUid = uid.slice(0, sepIdx);
      const group = polyGroups.get(baseUid);
      if (group) {
        const idx = group.subUids.indexOf(uid);
        if (idx >= 0) group.subUids.splice(idx, 1);
        if (group.subUids.length === 0) polyGroups.delete(baseUid);
      }
    }
  }
}


// ============================================================
// Scope info text formatter
//
// Builds a detailed pipe-delimited label string for the SVG
// scope _infoText element. Used by both primeSynthScope (at
// assign time with static params) and live voice updates
// (with real-time freq/amp values).
//
// @param {object} params   - synth params from AST or voice.params
// @param {string} wave     - waveform type ("sine", "square", etc.)
// @param {object} live     - optional live values { freqHz, amp }
// @returns {string}        - pipe-delimited info label
// ============================================================

/**
 * Format a pattern value for display (handles nested patterns).
 * If currentHz is provided, highlights matching values in red.
 */
function formatPatternValueForDisplay(v, isActive = false) {
  if (v && typeof v === "object" && v.type === "pattern") {
    // Nested pattern - show without internal highlighting (would need sub-index tracking)
    const name = v.name || "Pseq";
    const vals = (v.values || []).map(inner => formatPatternValueForDisplay(inner, false)).join(",");
    const str = `${name}(${vals})${formatPatternRepeats(v.repeats)}`;
    return isActive ? `<span style="color:#c00;font-weight:bold;pointer-events:none">${str}</span>` : str;
  }
  // rand()/hrand() etc kept as funcCall for per-use evaluation
  if (v && typeof v === "object" && v.type === "funcCall" && v.name) {
    const str = `${v.name}(${Array.isArray(v.args) ? v.args.join(",") : ""})`;
    return isActive ? `<span style="color:#c00;font-weight:bold;pointer-events:none">${str}</span>` : str;
  }
  // Array (chord) within pattern
  if (Array.isArray(v)) {
    const chordStr = v.map(inner => formatPatternValueForDisplay(inner, false)).join(",");
    if (isActive) {
      return `<span style="color:#c00;font-weight:bold;pointer-events:none">[${chordStr}]</span>`;
    }
    return `[${chordStr}]`;
  }
  // Single note
  if (isActive) {
    return `<span style="color:#c00;font-weight:bold;pointer-events:none">${v}</span>`;
  }
  return String(v);
}

function formatScopeInfo(params, wave, live = {}) {
  const p = params || {};
  const parts = [];

  // Waveform type (always show)
  parts.push(String(wave || p.wave || "sine"));

  // Frequency / pitch
  if (p.freq != null && typeof p.freq === "object" && p.freq.type === "pattern") {
    // Pattern: show all values with current one highlighted in bold red
    const patternName = p.freq.name || "Pseq";
    const values = p.freq.values || [];
    const currentIndex = live.freqIndex;

    const repSuffix = formatPatternRepeats(p.freq.repeats);
    if (currentIndex != null && values.length > 0) {
      // Highlight by index - only the value at currentIndex gets highlighted
      const highlighted = values.map((v, i) => formatPatternValueForDisplay(v, i === currentIndex)).join(",");
      parts.push(`${patternName}[${highlighted}]${repSuffix}`);
    } else {
      // No live value yet - show plain pattern
      parts.push(`${patternName}[${values.map(v => formatPatternValueForDisplay(v, false)).join(",")}]${repSuffix}`);
    }
  } else if (live.freqHz != null) {
    // Live resolved frequency from oscillator node (non-pattern)
    // Check if original param was a note name - if so, show that instead of Hz
    const origFreq = p.freq;
    const origIsNoteName = origFreq && /^~?[A-Ga-g][#b]?-?\d+/.test(String(origFreq));

    let freqDisplay;
    if (live.freqHz === "rest") {
      freqDisplay = "rest";
    } else if (origIsNoteName) {
      // Preserve note name display
      freqDisplay = String(origFreq).replace(/^~/, '');
    } else if (Array.isArray(live.freqHz)) {
      freqDisplay = `chord[${live.freqHz.map(f => Math.round(f)).join(",")}]`;
    } else {
      freqDisplay = `${Math.round(live.freqHz)}Hz`;
    }
    parts.push(freqDisplay);
  } else if (p.freq != null) {
    // Static from params
    if (Array.isArray(p.freq)) {
      parts.push(`chord[${p.freq.map(v => formatPatternValueForDisplay(v, false)).join(",")}]`);
    } else {
      // Check if it's a note name (starts with ~ or matches note pattern)
      const freqStr = String(p.freq);
      const isNoteName = /^~?[A-Ga-g][#b]?-?\d+/.test(freqStr);
      if (isNoteName) {
        // Show note name (strip ~ prefix for cleaner display)
        parts.push(freqStr.replace(/^~/, ''));
      } else {
        // Show as Hz
        const hz = pitchToHz(p.freq);
        parts.push(`${Math.round(hz)}Hz`);
      }
    }
  }

  // Filter (type, cutoff, Q)
  if (p.filter && typeof p.filter === "object") {
    const type = p.filter.type || p.filter.mode || "lp";
    const items = [type];

    const cf = p.filter.freq ?? p.filter.cutoff;
    if (cf != null) {
      if (typeof cf === "object" && cf.type === "pattern") {
        items.push(`${cf.name}`);
      } else {
        items.push(`F:${stripZ(cf)}`);
      }
    }
    if (p.filter.q != null) {
      items.push(`Q:${stripZ(p.filter.q)}`);
    }
    parts.push(items.join(" "));
  }

  // Envelope (compact A/D/S/R)
  if (p.env && typeof p.env === "object") {
    const labels = [];
    if (p.env.a != null || p.env.attack != null)
      labels.push(`A:${stripZ(p.env.a ?? p.env.attack)}`);
    if (p.env.d != null || p.env.decay != null)
      labels.push(`D:${stripZ(p.env.d ?? p.env.decay)}`);
    if (p.env.s != null || p.env.sustain != null)
      labels.push(`S:${stripZ(p.env.s ?? p.env.sustain)}`);
    if (p.env.r != null || p.env.release != null)
      labels.push(`R:${stripZ(p.env.r ?? p.env.release)}`);
    if (labels.length > 0) parts.push(`env ${labels.join(" ")}`);
  }

  // Duration
  if (p.dur != null) {
    if (typeof p.dur === "object" && p.dur.type === "pattern") {
      const patternName = p.dur.name || "Pseq";
      const values = p.dur.values || [];
      const currentIndex = live.durIndex;

      if (currentIndex != null && values.length > 0) {
        // Highlight by index - only the value at currentIndex gets highlighted
        const highlighted = values.map((v, i) => formatPatternValueForDisplay(v, i === currentIndex)).join(",");
        parts.push(`dur ${patternName}[${highlighted}]${formatPatternRepeats(p.dur.repeats)}`);
      } else {
        // No live value yet - show plain pattern
        parts.push(`dur ${patternName}[${values.map(v => formatPatternValueForDisplay(v, false)).join(",")}]${formatPatternRepeats(p.dur.repeats)}`);
      }
    } else {
      parts.push(`dur ${p.dur}`);
    }
  }

  // Amplitude
  const ampVal = live.amp ?? p.amp;
  if (ampVal != null) {
    parts.push(`amp ${Number(ampVal).toFixed(3)}`);
  }

  // Playback target clients
  if (live.playbackClients?.length) {
    parts.push(`→ ${live.playbackClients.join(", ")}`);
  }

  return parts.join(" | ");
}

/** Strip leading zero from decimal numbers for compact display */
function stripZ(v) {
  if (typeof v !== "number") return String(v);
  const s = String(v);
  return s.startsWith("0.") ? s.slice(1) : s;
}


// ============================================================
// Prime synth overlay (called during assignCues)
//
// Exported for cueDispatcher.
// Delegates entirely to primeSynthScope which renders the
// SVG-native scope display with detailed info text.
// ============================================================
export function primeSynthOverlay(ast, cueElement) {
  if (!ast || !cueElement) return;

  // Extract params from AST for the scope
  const params = {};
  for (const arg of (ast.args || [])) {
    const key = arg.key || arg.type;
    if (key && arg.value !== undefined) {
      params[key] = arg.value;
    }
  }

  // Check if overlay/scope is requested (the overlay param controls visibility)
  const overlayFlag = Number(params.overlay ?? 2);
  if (overlayFlag <= 0) return;

  // Prime the SVG scope display (handles its own double-prime guard)
  primeSynthScope(ast, cueElement, params);
}


// ============================================================
// 🎯 Prime synth oscilloscope (called during assignCues)
// Renders a static representative waveform into the score SVG.
// Default: "self" (the cue element). Use waveform:none to suppress.
// ============================================================
export function primeSynthScope(ast, cueElement, params) {
  if (!ast || !cueElement) return;

  // Extract params if not already provided
  if (!params) {
    params = {};
    for (const arg of (ast.args || [])) {
      const key = arg.key || arg.type;
      if (key && arg.value !== undefined) {
        params[key] = arg.value;
      }
    }
  }

  const waveformParam = params.waveform ?? "self";
  if (waveformParam === "none" || waveformParam === "0") return;

  // Avoid double-priming
  if (cueElement._synthScopePrimed) return;
  cueElement._synthScopePrimed = true;

  const wave = params.wave ?? "sine";
  // Use same uid fallback logic as handleSynthCue for consistency
  let uid = params.uid || "";
  if (!uid) {
    if (cueElement?.id) {
      // Hash element id same way as handleSynthCue
      uid = `synth-${cueElement.id.substring(0, 50).replace(/[^a-zA-Z0-9]/g, '_')}`;
    }
  }
  if (!uid) uid = "synth";
  const scopeUid = `scope-${uid}`;

  const svg = cueElement.ownerSVGElement;
  if (!svg) return;

  // A DSL extent:N param supersedes the scope's own extent visual — the
  // draggable dsl-extent-line (cueDispatcher) renders it instead.
  const dslExtentW = Number(params.extent);
  const hasDslExtent = dslExtentW > 0;

  // Calculate extent from parent group if element is inside a group (SVG-authored extent)
  // But NOT for audio objects on the interaction layer
  let extent = 200; // default
  const isGroupHost = cueElement.tagName?.toLowerCase() === 'g';
  const parent = cueElement.parentElement;
  if (!hasDslExtent && !isGroupHost && isAuthoredExtentGroup(parent, cueElement)) {
    // Parent is a group - calculate extent from group's right edge to element's right edge
    try {
      const elBbox = cueElement.getBBox();
      const groupBbox = parent.getBBox();
      const elRight = elBbox.x + elBbox.width;
      const groupRight = groupBbox.x + groupBbox.width;
      if (groupRight > elRight) {
        extent = groupRight - elRight;
      }
    } catch { /* ignore bbox errors */ }
  }

  // Detailed info label using shared formatter
  const scopeInfo = formatScopeInfo(params, wave, {
    playbackClients: getPlaybackClientsFromEl(cueElement)
  });

  renderScope(svg, waveformParam, scopeUid, {
    element: cueElement,
    info: scopeInfo,
    wave: wave,
    color: getSynthTypeColor(params),
    extent: extent,
    // No crossing region when another object fires this cue (trig:<uid>):
    // the extent lines would only clutter the row of targets.
    skipExtent: hasDslExtent || isObjectTrig(params.trig),
    backgroundColor: cueElement?.dataset?.playbackBackground || null
  });

}